import { BRAIN, STATE } from '../actors/ai.js';
import { streamRng } from '../math/rng.js';
// ⚠ ONE import purely to CLOSE A MIRROR. `BREAK.carSpeed` / `BREAK.carRadius`
// answer the question "is this car actually driving AT something, and is it
// close enough to have hit it" for street furniture, and a car that flattens a
// bench must flatten the man standing on it — see {@link VEHICLE.runOverSpeed}.
// `Breakables.js` imports nothing but `math/rng.js`, so this costs no three.js
// and no cycle. It is an IMPORT rather than the restated constants `links.js`
// keeps of `countryroads.js`, because unlike those two these are EXPORTED and
// there is therefore no excuse for a second copy.
import { BREAK } from './Breakables.js';

/**
 * @file CARS THAT DIE, AND THE PEOPLE THEY KILL ON THE WAY. A car carries hit
 * points, loses them to walls, to other cars and to gunfire, trails smoke from
 * the bonnet below half, catches fire below a fifth, and at zero throws its
 * panels across the street, damages everything standing near it and settles
 * into a charred wreck that burns out. And, at any point before that, a car
 * that is MOVING runs over whoever is in front of it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO three.js — EVERY GPU OBJECT IS INJECTED
 * ─────────────────────────────────────────────────────────────────────────────
 * Same mould as `traffic/Traffic.js`, `systems/Driving.js`, `systems/Carjack.js`
 * and `systems/Breakables.js`: the fleet, the particle batch, the actor manager,
 * the bus and the faction table all arrive as arguments and every one of them
 * may be `null`. `fleet: null, fx: null` is a legal, fully-simulating damage
 * model — which is the whole reason `tests/vehicledamage.test.mjs` can prove the
 * arithmetic, the chain reaction and the determinism in node with no GPU. The
 * three imports are `actors/ai.js` (an enum), `math/rng.js` (arithmetic) and
 * `systems/Breakables.js` (two numbers, see the import note above) — none of
 * them touches a GPU, a DOM or a global.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT POLLS THE WORLD; NOBODY REGISTERS A CAR
 * ─────────────────────────────────────────────────────────────────────────────
 * Cars in this game are born and retired by four different owners — `Traffic`'s
 * driving ring, `Traffic`'s parked pool, `Driving`'s player body and `Driving`'s
 * coasting body — and a car crosses between them (a carjack is a driving slot
 * becoming a parked slot becoming the player's car) without any of them telling
 * anybody. A registration API would therefore need a hook on every one of those
 * transitions, and the first one anybody forgot would leak a health bar onto a
 * car that no longer exists.
 *
 * So {@link createVehicleDamage} walks all four every step and RECONCILES. A car
 * that vanished is forgotten with its panels and its smoke; a car that appeared
 * starts at {@link VEHICLE.maxHp}.
 *
 * ⚠ **A `CarFleet` handle is RECYCLED.** `CarFleet#removeCar` pushes the handle
 * onto a free list and the next `addCar` pops it, so "handle 7" an hour into a
 * session is a different car every few minutes — and a fresh saloon inheriting
 * the burning wreck's 4 hp would explode on the first kerb it touched. The fleet
 * therefore exposes `serialOf(handle)`, a counter bumped on every `addCar`, and
 * the identity we track is the PAIR `(handle, serial)`. A serial that moved is a
 * new car, full stop — and it is caught on the SAME step, which the
 * "disappeared for a whole step" fallback (all we have when `fleet` is null)
 * structurally cannot do.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE STEP ORDER, AND WHY IT IS THIS ORDER
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 *   1   reconcile   who exists, at what pose, with what health
 *   1b  run over    a car that is moving is a weapon: who is under it
 *   2   burn        a burning car loses hp on its own
 *   3   blasts      drain the queue: actors, the player, other cars
 *   4   panels      the ballistic free bodies, then their poses to the fleet
 *   5   particles   emit, age, and hand the survivors to `fx`
 * ```
 *
 * • **Reconcile first** or step 2 burns a car that despawned last step and step 3
 *   blasts one that never arrived. Everything below reads the pose this step
 *   established; nothing below re-reads `traffic`.
 * • **Run over IMMEDIATELY after it**, because it reads exactly one thing and
 *   that thing is what the reconcile just produced: the pose DELTA, which is
 *   this system's only measure of how fast a car is going (see
 *   {@link CarRec.px}). Adjacency is the point — put it below the burn and the
 *   velocity it reads is still correct but is no longer obviously the pose the
 *   line above it wrote. It is also deliberately AHEAD of the blasts: somebody
 *   a car is about to kill should die under the wheels, on the frame the wheels
 *   arrived, rather than a step later inside the explosion that follows.
 * • **Burn before the blasts**, because burning is the thing that CAUSES a blast
 *   nobody triggered: fire is terminal, so a car the player set alight and drove
 *   away from goes up on its own about {@link VEHICLE.fireAt} ×
 *   {@link VEHICLE.maxHp} / {@link VEHICLE.burnDps} seconds later. Running the
 *   queue first would hold that explosion over to the next step for no reason.
 * • **Blasts before the panels**, because a blast TEARS panels — resolving it
 *   after the ballistic step would leave every panel it threw sitting at its
 *   launch point for one frame, which at 9 m/s is a visible stutter.
 * • **Particles last**, because they are the only step that reads the finished
 *   state of everything above it: a car that exploded this step must emit its
 *   wreck's flame this step, not next.
 *
 * ⚠ ONE deliberate exception: the FIREBALL is emitted in `destroy()`, at the
 * instant of detonation, not in step 5. Step 5 emits from STATE — a rate derived
 * from a car's current hp, re-derivable on any later step. A fireball is an
 * EVENT with nothing behind it to re-derive, so deferring it would make the
 * flash lag its own explosion whenever `explode()` is called from outside
 * `update`. See {@link emitFireball}.
 *
 * ⚠ THE BLAST QUEUE IS A QUEUE, NEVER RECURSION. A car park full of cars is a
 * chain of arbitrary length, and a recursive `explode` would blow the JS stack
 * on the pathological case and — worse — resolve the innermost blast first, so
 * the damage would arrive in the wrong order. {@link VEHICLE.explodeMaxChain} is
 * the depth cap that backs it up.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE DOES NOT RETIRE A SLOT
 * ─────────────────────────────────────────────────────────────────────────────
 * A wrecked car is still a `Traffic` driving slot or a `Driving` body, and
 * converting one into the other is a handover with exactly one legal path
 * (`Traffic.takeDriving`, see `docs/vehicles.md`). Reaching into either system
 * from here would be a second path, and two of those is how the player's car
 * ends up existing twice. We emit `'car:wrecked'` and `'car:exploded'` and let
 * `Gameplay` decide; until it does, the wreck sits there burning, which is
 * exactly what it should look like.
 *
 * ⚠ {@link VEHICLE.wreckLife} DOES NOT CHANGE THAT. A wreck that has stood for
 * a minute emits `'car:despawn'` — once, latched — and goes on standing there
 * until somebody with the authority to retire a slot acts on it. The record is
 * dropped by {@link reconcile} when the FLEET says the handle is dead, never by
 * the timer, or the next poll would re-mint the same car at full health.
 *
 * @module engine/systems/VehicleDamage
 */

/** @typedef {import('../types.js').Rng} Rng */

/**
 * Every number the damage model owns. Metres, seconds, m/s, hit points.
 *
 * Nothing here is DERIVED from another table — this is the one system in the
 * vehicle stack with no physical model behind it, so these are all authored. The
 * derivations that DO exist are inside it, and each is called out below.
 */
export const VEHICLE = Object.freeze({
  /** Every car, from a hatchback to a police cruiser. One number on purpose:
   *  a per-archetype health table would mean the player learning which paint
   *  jobs are tough, and nothing on screen tells them. */
  maxHp: 100,

  // ── the two thresholds ─────────────────────────────────────────────────────
  /**
   * hp FRACTION below which the bonnet smokes. Half is the readable place for
   * it: it is the point a player would guess, so the first time a car starts
   * trailing smoke it reads as "this is half dead" and not as a random event.
   * STRICTLY below — a car sitting at exactly 50% is a car that has been hit,
   * not a car that is failing.
   */
  smokeAt: 0.5,
  /** ...and below which it burns. A fifth is roughly two more shunts, which is
   *  the window the player has to get out. Also strictly below. */
  fireAt: 0.2,
  /**
   * hp/second a BURNING car loses with nobody touching it. It is what makes
   * fire terminal rather than cosmetic — and it is the only number in the game
   * that says how long the player has to act on the cue.
   *
   * ⚠ **THE BAIL-OUT WINDOW IS `fireAt * maxHp / burnDps`** — 10 seconds — and
   * it is those two constants TOGETHER. Move either and the window moves; there
   * is deliberately no third constant holding it, because a separately-tabled
   * timer would drift out of step with the threshold that starts it. 2 dps is
   * chosen so the product lands on a round 10 s; the window is the number that
   * was decided, and this is the constant that expresses it.
   *
   * ⚠ **IT HAS BEEN RAISED TWICE, BOTH TIMES BECAUSE THE FIRE READ AS A VERDICT
   * RATHER THAN AS A CHANCE TO LEAVE.** It shipped at 5 dps (4.0 s), which was a
   * cue you could only act on if you were already expecting it. 3 dps (6.7 s)
   * was defensible on paper — brake, door, run past {@link explodeRadius} — and
   * was still reported in play as "the car explodes too fast to get out of",
   * because the paper version starts the clock at the moment the player NOTICES
   * the fire and the real one starts it at `fireAt`, several tenths earlier, with
   * a car still doing 17 m/s under them. 10 s absorbs that gap: it is long enough
   * to react, brake from the limiter, get out and clear the radius on foot, and
   * still not long enough to carry on to the mission marker — which is the line
   * this number has always been drawn against.
   */
  burnDps: 2,

  // ── collisions ─────────────────────────────────────────────────────────────
  /**
   * m/s of speed a wall impact has to take off the body before it costs any
   * health. Deliberately just above `BREAK.carSpeed` (3.4) and `DRIVE.exitSpeed`
   * (3): scraping a kerb while parking, or nosing a bollard at walking pace,
   * must be free, and everything you would call "hitting" something is well past
   * it.
   */
  wallImpactMin: 3.5,
  /** hp per m/s past {@link wallImpactMin}. A 12 m/s crash into a building —
   *  which stops the car dead — is (12 − 3.5) × 3.5 ≈ 30 hp, so a wall can kill
   *  a car in four full-speed hits and never in one. */
  wallDamage: 3.5,
  /** A car is softer than a wall, so it yields sooner... */
  carImpactMin: 2.0,
  /** ...and hurts more, because BOTH cars take this — a shunt is one impact
   *  billed twice, which is what makes traffic pile-ups escalate. */
  carDamage: 5.0,
  /**
   * A MULTIPLIER on a round's own damage, applied when the round would have hit
   * the DRIVER and hits the car instead (`Gameplay.js`:
   * `vehicles.damage(handle, amount * VEHICLE.bulletToCar, …)`). Not an absolute
   * hp figure: a multiplier scales with the weapon, so a tank shell hurts a car
   * more than a pistol round does, which a flat number throws away.
   *
   * ⚠ **ANCHORED TO THE CAST'S WEAPONS, exactly like `FIRE.range`** (see
   * `CLAUDE.md`). Move the damage on a `*.enemy.json` and this is re-derived,
   * not left alone. The table it is derived against today:
   *
   * ```
   *   police_officer  10 dmg / 0.9 s   → 3.5 to the car  → 29 rounds to destroy
   *   swat            13 dmg / 0.55 s  → 4.55            → 22
   *   tank            16 dmg / 1.8 s   → 5.6             → 18
   * ```
   *
   * ⚠ THE FIGURE IS `stats.fire.damage`, NEVER `stats.damage`. They are
   * different weapons: `stats.damage` is the CONTACT attack (the tank's is 25)
   * and `fire.damage` is the round (16). Only rounds come through here, and
   * reading the wrong field off the same JSON overstates a tank by 56% — this
   * table shipped with exactly that mistake in it.
   *
   * At 0.35 a police officer needs 15 rounds to make a car smoke, 23 to set it
   * alight and 29 to destroy it: ~13 s and ~26 s of ONE officer's uninterrupted
   * fire, or ~4 s and ~9 s with the three a real wanted level puts on you — and
   * that is BEFORE misses, since `rollShot` means not every round lands, so the
   * felt numbers are longer still.
   *
   * ⚠⚠ **THE INVARIANT IT EXISTS TO HOLD: a car must absorb strictly MORE
   * rounds than the player survives on foot.** The player has 100 hp and takes
   * 10 a round, i.e. 10 hits. It shipped at 1.6, where a round cost the car
   * 16 hp and **7 rounds destroyed it** — a car was a WORSE place to be under
   * fire than standing in the open, which inverts the whole feature and
   * contradicts `Gameplay.js`'s own rule that the car is what protects you.
   * Parking in a firefight cannot be a decision if the car is made of paper.
   * `tests/vehicledamage.test.mjs` pins the INVARIANT rather than the number,
   * because a test on 0.35 would not have caught that.
   *
   * ⚠⚠⚠ UNPLAYTESTED. 0.35 is derived from a comparison the old value provably
   * failed, which makes it defensible, not verified — nobody has yet driven a
   * car under police fire.
   *
   * ({@link createVehicleDamage}'s `damageAt` is a REAL seam but not this
   * constant's caller — nothing in `src/` calls it. It is the door a manual
   * player shot would come through, and that trigger was deleted on
   * 2026-08-07; see `docs/actors.md`.)
   */
  bulletToCar: 0.35,

  // ── running people over ────────────────────────────────────────────────────
  /**
   * m/s below which a car hits NOBODY. **It is `BREAK.carSpeed`, imported, not
   * a number of our own** — a car slow enough to nose a bench aside without
   * breaking it is a car slow enough to nose a pedestrian aside without killing
   * them, and two constants answering one question is how they come to
   * disagree. (`Breakables.js` derives it from `DRIVE.exitSpeed` 3: the speed
   * you can step out at must not also be the speed that kills people.)
   *
   * ⚠ **IT IS A GAMEPLAY RULE FIRST AND AN OPTIMISATION SECOND, IN THAT ORDER,
   * AND IT IS ALSO BOTH.** As a rule: a cruiser easing up to a stop line
   * through a crowd must not mow the crowd down, and traffic in this city is
   * stationary a great deal of the time (junction claims, headway braking).
   * As an optimisation: it is a subtract and a compare that skips the ENTIRE
   * inner loop for every stopped car, which is what keeps the brute-force sweep
   * below honest.
   */
  runOverSpeed: BREAK.carSpeed,
  /**
   * The contact box's LATERAL HALF-WIDTH, metres — a bumper, not a body, and
   * {@link BREAK.carRadius} for exactly the same reason {@link runOverSpeed} is
   * `BREAK.carSpeed`. The victim's own radius is added on top
   * (`EnemyManager#radius`), so a wide actor is hit sooner.
   *
   * ⚠ **THE UNITS ARE PUBLISHED AND THEY ARE HALF-WIDTH, NOT RADIUS.** The
   * LONGITUDINAL half-extent is not this number at all: it is the car's own
   * `rec.L / 2`, read off the chassis `Traffic#archChassis` already publishes,
   * so nothing here tables a car length. See {@link createVehicleDamage}'s
   * `runOver` for the frame the test is done in. This is also the reading
   * `Breakables.js` gives `carRadius` (a half-WIDTH for its overlap test), so
   * the shared constant is now described the same way on both sides.
   *
   * ⚠ **IT SHIPPED AS A SPHERE ABOUT THE CAR'S ORIGIN AND THAT WAS REVERSED BY
   * MEASUREMENT, NOT BY TASTE.** The old comment here called the circle a
   * recorded trade whose only cost was "being clipped by a bumper you are
   * standing beside rather than in front of" — i.e. it predicted false
   * POSITIVES at the flank. `/play/cardamage` measured the opposite: the real
   * cost was false NEGATIVES at the NOSE. A 4.7 m car's contact zone was a
   * ~1.45 m circle about its centre, so the leading ~1.2 m of the car passed
   * clean through a pedestrian before anything registered and you had to
   * nearly CENTRE the car on somebody to hit them — the feature failing at the
   * one thing it exists to do. A measurement beats a comment's guess.
   */
  runOverReach: BREAK.carRadius,
  /**
   * hp per m/s of speed past {@link runOverSpeed}. A LINEAR ramp anchored at
   * both ends, and both ends are real numbers rather than taste:
   *
   * ```
   *   at runOverSpeed 3.4        0 hp   the gate: a crawl hurts nobody
   *   at DRIVE.topSpeed 17     136 hp   (17 - 3.4) * 10
   * ```
   *
   * ⚠ **THE TOP END IS DERIVED FROM THE CAST, THE SAME DISCIPLINE AS
   * {@link bulletToCar} AND `FIRE.range`:** a car at the limiter must kill
   * anything in this game that walks, outright. The toughest thing on foot is
   * `swat` at **130 hp** (`swat.enemy.json`), so the requirement is
   * `(17 − 3.4) × runOverDamage ≥ 130`, i.e. `runOverDamage ≥ 9.56`. **10** is
   * that, rounded up to a number a reader can do in their head, and the ~5%
   * it adds is margin rather than meaning. What the ramp then says about the
   * rest of the cast — all of it falling out of the one constant:
   *
   * ```
   *   civilian  30 hp   dies at  6.4 m/s   (23 km/h — any real driving speed)
   *   gang      55 hp   dies at  8.9 m/s
   *   police    70 hp   dies at 10.4 m/s
   *   swat     130 hp   dies at 16.4 m/s   (the limiter, and only just)
   * ```
   *
   * which is the shape that was wanted: a pedestrian is killed by anybody
   * driving normally, and armour is worth something against a car.
   *
   * ⚠ **`src/engine/` MAY NEVER IMPORT `src/game/`**, so the 130 and the 17 are
   * NOT importable here and are written out by hand — the `PLANT_FRAC` idiom
   * (`docs/stamina.md`). `tests/vehicledamage.test.mjs` closes both mirrors by
   * reading the REAL `*.enemy.json` files and the REAL `DRIVE.topSpeed`: move
   * a `hp` in the cast or the car limiter and a test fails, rather than the
   * derivation quietly stopping being true.
   *
   * ⚠ The `tank` (220 hp) is NOT in that list and is not meant to be. It is a
   * rig, not a pedestrian, and "you cannot kill a tank by driving into it" is
   * the correct answer rather than a gap.
   *
   * ⚠ NOT capped at the top. A car can exceed {@link DRIVE.topSpeed} coasting
   * downhill, and a clamp there would be a third number nobody could see.
   */
  runOverDamage: 10,
  /**
   * m/s a car BELOW {@link runOverSpeed} displaces a body it is overlapping,
   * sideways, out of its own footprint. No damage, no blood, no crime — this is
   * the same argument `Breakables.js` makes for the coasting car: **a car that
   * passes through a body is the sim disagreeing with itself**, and at a crawl
   * the honest resolution is that people get out of the way.
   *
   * ⚠ **"SIDEWAYS" MEANS ALONG THE CAR'S LATERAL AXIS, AND THAT IS THE WHOLE
   * WORD.** It shipped pushing along `actor − car origin`, which for the common
   * case — a body directly in front of the bumper — is dominated by the
   * ALONG-TRACK component, so a crawling car HERDED people down the road ahead
   * of it like a snowplough instead of shouldering them aside. Measured on
   * `/play/cardamage`. The push is on the lateral axis now, toward whichever
   * side the body is already on; see `nudge`.
   *
   * ⚠ A PARKED car needs none of this and gets none: `Traffic` registers
   * `PARK_CIRCLE` colliders for parked cars and NOTHING for driving ones
   * (`docs/vehicles.md`), so the pedestrian collision grid already pushes
   * people off a parked saloon. This exists for the case the grid cannot see —
   * a car under power, moving too slowly to kill.
   *
   * ⚠⚠ **AN ADMITTED FEEL GUESS.** Nobody has watched a car nose through a
   * crowd; 1.6 m/s is a brisk sidestep and it is not derived from anything.
   * Saying so is the project's own preference (see {@link burnDps}) over
   * inventing a derivation for it.
   */
  runOverNudge: 1.6,

  // ── the explosion ──────────────────────────────────────────────────────────
  /** Metres. Blast damage falls LINEARLY to zero here — see {@link falloffAt}. */
  explodeRadius: 9,
  /** Damage at the very centre, to an ACTOR or to the player. 140 kills every
   *  human in the cast outright and is survivable from about 4 m out, which is
   *  the "I nearly got away with that" distance. */
  explodeDamage: 140,
  /**
   * ...and to another CAR, on the same falloff.
   *
   * ⚠ **THIS NUMBER IS DERIVED FROM {@link fireAt} AND THE FALLOFF, NOT CHOSEN.**
   * What a blast has to produce is a SEQUENCE of explosions, not one
   * simultaneous flash, and because the falloff is linear a single constant buys
   * exactly that shape:
   *
   * ```
   *     0 m   bumper to bumper    110      a healthy car destroyed OUTRIGHT
   *   0.8 m   ...that boundary    100      past here a full car survives the bang
   *   2.0 m   the next bay along   85.6    leaves it at 14.4 hp — UNDER fireAt,
   *                                        so it catches fire and goes up on its
   *                                        own `burnDps` seconds later
   *   2.5 m   ...that boundary     80      exactly fireAt: the last doomed range
   *   4.5 m   normal parking       55      leaves it at 45 hp: smoking, ALIVE
   *   9.0 m   explodeRadius         0      nothing
   * ```
   *
   * So cars touching each other chain, a car a lane away is merely hurt, and the
   * chain arrives spread over SECONDS because the middle rows go through the
   * burn timer rather than through this blast.
   *
   * It shipped at 70, where the top row was 70 and NOTHING chained: a blast
   * could not destroy a healthy car at any distance, so the chain was a feature
   * with a test propping it up and no way to see it in the game. The other
   * failure is the opposite one — at {@link explodeDamage} 140 every car inside
   * the radius goes at once, which is one bang and a car park full of wrecks.
   * 110 is where the middle rows do the work.
   */
  explodeCarDamage: 110,
  /** Queue DEPTH cap, not a count cap. A blast whose depth reaches this is
   *  dequeued without resolving, which is what makes the chain provably
   *  terminate even if a future caller invents a cycle. */
  explodeMaxChain: 8,

  // ── panels ─────────────────────────────────────────────────────────────────
  /** hp taken in ONE hit that rips a panel off. 22 is above the biggest shunt
   *  (a 6 m/s closing speed = 20) and below a 10 m/s wall, so panels come off in
   *  real crashes and never in traffic. */
  partTearMin: 22,
  /** rad/s a torn panel tumbles at. */
  partSpin: 6,
  /** ...and m/s it leaves the car at, along the impact direction. */
  partToss: 5,
  /** m/s² for a panel in flight. Well over 9.81, same reasoning as
   *  `BREAK.gravity`: a short blunt arc reads as sheet metal, a long floaty one
   *  reads as paper. */
  gravity: 18,
  /** ...and how hard the EXPLOSION throws one. Nearly double {@link partToss}
   *  because the blast throws it UP as well as out. */
  blastToss: 9,
  /**
   * Hard cap on simulated panels. A RING BUFFER (see the header): panel 97
   * overwrites panel 1's slot, and panel 1 simply freezes where it lay.
   *
   * THE ARITHMETIC IS THE **BLAST** SET (`blastTearable`), because the ring is
   * sized by the worst case and the worst case is a chain of detonations: a
   * two-row car sheds **9** panels there (hood + 4 doors + 4 wheels), and the
   * longest chain {@link explodeMaxChain} permits destroys 9 cars (8 blasts
   * resolve, and the ninth car is destroyed by the last of them) — so the
   * worst legitimate demand is `9 × 9 = 81`, which fits in 96 with room to
   * spare. 96 stays. A one-row hatchback sheds 7, so the real worst case is
   * lower again.
   *
   * The COLLISION set (`crashTearable`, 5 panels) does not enter this sum and
   * cannot raise it: a panel a crash took is already in `rec.torn`, so the
   * blast that follows throws one fewer, never one more.
   *
   * ⚠ Raise this only if that product exceeds it. Beyond the cap nothing
   * breaks — the oldest panel stops being simulated and freezes where it lay,
   * which under a 60-car pile-up is the behaviour you want anyway.
   */
  maxParts: 96,

  // ── the wreck ──────────────────────────────────────────────────────────────
  /** Seconds a wreck burns after the blast... */
  burnTime: 9,
  /** ...then smokes only, for this long, and then it is cold and free. */
  smoulderTime: 22,
  /**
   * Seconds from DETONATION to the wreck being asked for. At `wreckT` this the
   * system emits `'car:despawn'` ONCE and the wiring retires the slot — this
   * file still does not remove a car (see the header).
   *
   * Until this existed a wreck was collected by DISTANCE only (`Traffic`'s
   * rings: a guest parked slot past 120 m, a budgeted bay past 150 m), so a
   * player who stayed put stood next to a charred shell forever.
   *
   * ⚠ **IT MUST EXCEED `burnTime + smoulderTime`** (31 s), and the suite asserts
   * the RELATIONSHIP rather than the number so a retune of either phase breaks
   * a test instead of quietly popping a car out of its own plume. The 29 s of
   * cold shell between the smoke stopping and the removal is not slack: it is
   * the beat in which the wreck is just SCENERY, and it is the whole reason the
   * removal reads as "the wreck was cleared" rather than as "the fire went out
   * and took the car with it". Cut it to nothing and the disappearance becomes
   * the last frame of the fire, i.e. part of the explosion.
   */
  wreckLife: 60,
  /** Charred paint, DAY channel, LINEAR (the fleet's tints are linear — see
   *  `CarFleet#setTint`). Not pure black: a zero tint reads as a hole in the
   *  world at night, where a hair of grey still catches the night channel. */
  charDay: Object.freeze([0.035, 0.032, 0.03]),
  /** ...and the NIGHT channel, darker again. */
  charNight: Object.freeze([0.012, 0.011, 0.01]),

  // ── smoke and fire ─────────────────────────────────────────────────────────
  /** Smoke puffs per second at FULL smoke (a car at 0 hp that has not gone up
   *  yet, or a smouldering wreck). */
  smokeRate: 9,
  /** Fire puffs per second at full fire. Faster than smoke because a flame is
   *  short-lived — see {@link fireLife}. */
  fireRate: 22,
  /** Seconds a smoke puff lives. */
  puffLife: 2.2,
  /** m/s a puff climbs. */
  puffRise: 1.6,
  /** m/s its radius grows — a puff that does not expand reads as a sprite. */
  puffGrow: 0.9,
  /** ±m/s of horizontal wander, drawn once per puff. */
  puffDrift: 0.5,
  /** Seconds a flame lives. Short: a flame is a flicker, and a long-lived one
   *  drifting upward is smoke that forgot to change colour. */
  fireLife: 0.45,
  /** The fireball's own life — how long the BANG lasts, as distinct from the
   *  {@link burnTime} of the wreck it leaves. Short and violent; past about a
   *  second a fireball stops reading as an explosion and starts reading as a
   *  bonfire. */
  blastLife: 0.9,
  /** ...and the radius it fills. Deliberately UNDER {@link explodeRadius}: a
   *  fireball drawn out to the damage radius promises a blast that reaches
   *  further than the numbers do. */
  blastR: 5.5,
  /**
   * FIRE puffs one fireball spends, and it is a BUDGET in two currencies.
   *
   * • **Bloom.** `VehicleFx`'s `FX` header does this arithmetic: fire is
   *   ADDITIVE and overlaps itself, one puff peaks at 1.105 (cool) / 1.318 (hot)
   *   red at the `fireAlphaMax` 0.85 cap, and the cap exists precisely so that
   *   "a ten-puff explosion" is bounded near **~13 rather than unbounded**. Ten
   *   is that sanctioned number: the core saturates to white with a bloom halo
   *   several metres across, which IS the fireball, and the real sum is well
   *   under `n * peak` because the puffs overlap mostly in their skirts.
   * • **The ring.** {@link maxParticles} is 256 for the WHOLE CITY. At 10 fire
   *   plus {@link blastSmoke} 6, one blast costs 16 slots — so even the eight-car
   *   chain the depth cap allows spends 128, half the ring, and never erases
   *   every other car's smoke. A fireball generous enough to look right per car
   *   is a fireball that blanks the street during a pile-up.
   */
  blastPuffs: 10,
  /** ...and the SMOKE puffs it leaves hanging behind it. Alpha-blended, so
   *  these cost no bloom at all; six is what turns a flash into a column. */
  blastSmoke: 6,
  /** Hard cap on live particles. Ring buffer, same as the panels: under a
   *  pile-up the oldest puff is overwritten and the draw never grows. */
  maxParticles: 256,
});

/**
 * Which batch a particle belongs in. The `fx` batch takes two calls, not a
 * `kind` argument, so this is only ever our own bookkeeping byte.
 * @enum {number}
 */
export const CAR_FX = Object.freeze({ SMOKE: 0, FIRE: 1 });

/**
 * How much of `amount` lands `d` metres from a blast centred at radius `r`.
 * LINEAR, and ZERO at exactly `r` — not a smooth falloff, because a player has
 * to be able to learn the safe distance, and `1 − d/r` is the only curve where
 * "just outside it" reliably means "unhurt".
 *
 * @param {number} amount @param {number} d @param {number} r
 * @returns {number} 0 at `d >= r`, `amount` at `d = 0`.
 */
function falloffAt(amount, d, r) {
  if (!(r > 0) || d >= r) return 0;
  return amount * (1 - d / r);
}

/** @param {number} v @param {number} a @param {number} b */
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

/**
 * Ceiling on an emission accumulator, i.e. the most puffs one source can spend
 * in one step. Nothing at 60 Hz comes close ({@link VEHICLE.fireRate} is 22/s,
 * i.e. 0.37 per step) — this exists so a pathological `dt` cannot turn a `while`
 * into a hang.
 */
const PUFF_BURST = 8;

/** Half a turn about an axis — the attitude a panel lies flat in. */
const HALF_PI = Math.PI / 2;

/**
 * m/s under which a car counts as PARKED for the run-over pass, and therefore
 * does not even nudge. Module-private rather than a {@link VEHICLE} row because
 * it tunes nothing: it exists so that the standing cars in the city — the whole
 * parked pool (`TRAFFIC.maxParked` plus `guestParked`) plus however much of the
 * driving ring is sitting at a junction claim on any given step — do not each
 * walk the actor list every frame for a displacement of zero metres.
 * ⚠ NO COUNT IS QUOTED HERE ON PURPOSE. A draft said "~200 stationary cars",
 * which is about three times the real ceiling and was nobody's measurement; the
 * caps live in `TRAFFIC` and this file cannot import them (it would be a mirror
 * to keep in step for a number that only ever appears in a comment).
 * Any value under {@link VEHICLE.runOverSpeed} that a moving car clears is
 * correct; this one is a hair over the float noise a re-placed pose can carry.
 */
const CRAWL_MIN = 0.2;

/**
 * The player's collision radius, and it is a MIRROR: `EnemyManager#_step` reads
 * `player?.radius ?? 0.35` and this system is handed a position, not a
 * `Player`. Same rule as {@link VEHICLE.runOverDamage}'s two hand-written
 * numbers — move `Player#radius` and move this. It is only ever added to
 * {@link VEHICLE.runOverReach}, so being a few centimetres out changes when the
 * bumper connects and nothing else.
 */
const PLAYER_RADIUS = 0.35;

/**
 * ...and the fallback for an actor whose manager publishes no `radius` array
 * (`EnemyManager`'s own `TYPE_FALLBACK.radius`). Every real manager has one; a
 * stub in a dev scene may not.
 */
const ACTOR_RADIUS = 0.45;

/** The victim id {@link createVehicleDamage}'s `'car:ranover'` uses for the
 *  player, who has no manager slot. Published in the payload, so it is a
 *  contract with `Gameplay`'s subscriber and not an internal sentinel. */
const VICTIM_PLAYER = -1;

/**
 * THE SMOKE RAMP, and it runs DOWNWARD as the car gets worse.
 *
 * ⚠ `VehicleFxBatch#addSmoke`'s `grey` is **1 = pale wisp, 0 = near-black soot**
 * (`FX.smokePale` / `FX.smokeDark`, and `smokeDark`'s own comment is "the soot
 * of a car that is properly on fire"). It is a LERP KEY, not an intensity, and
 * that is the trap: every instinct says the number should climb with how bad
 * the car is, and climbing means PALER. It shipped inverted — the lightly
 * smoking car had the darkest plume and the burning one was near-white, worst
 * at night where a burning car should read as soot against a black sky and was
 * instead the brightest thing in the scene.
 *
 * So the values MUST descend down this list, and `tests/vehicledamage.test.mjs`
 * asserts that against `FX`'s own two tints rather than against these numbers —
 * if the fx contract ever flips its direction, the test breaks instead of the
 * picture.
 */
const SMOKE_GREY = Object.freeze({
  /** A car just past `smokeAt`: a thin pale wisp off the bonnet. */
  hurt: 0.95,
  /** ...and at the bottom of that band, about to catch. */
  failing: 0.6,
  /** Alight: proper soot. */
  burning: 0.25,
  /** A wreck in its flame phase — the blackest a continuous source goes. */
  wreck: 0.06,
  /** ...and the smoulder, which pales as the fire dies out under it. */
  smoulderFrom: 0.2,
  smoulderTo: 0.4,
  /** The fireball's own column: burning fuel, blacker than anything else. */
  blast: 0.04,
});

/**
 * THERE ARE TWO TEARABLE SETS, AND THE DISTINCTION IS THE POINT: **a crash
 * bends bodywork, a detonation dismantles the car.** They are separate
 * predicates rather than one widened predicate because the two facts below are
 * BOTH true, of DIFFERENT paths — and a single predicate can only hold one of
 * them.
 *
 * Both test by NAME, because a name is all `CarFleet#partsOf` publishes.
 *
 * ⚠ THE HISTORY, because it is what a future widening will re-break. Wheels
 * were excluded at first, "on purpose (a car on three wheels needs a
 * suspension model nobody has written)". Then the wreck requirement arrived —
 * a destroyed car must not still have a windscreen or tyres — and the
 * exclusion was lifted for BOTH callers on the reasoning that "nothing DRIVES
 * on three wheels here, because the only thing that loses one is a car that
 * has already exploded". That second argument was FALSE the moment it was
 * written: `tearOne` runs on any single hit at or above
 * {@link VEHICLE.partTearMin} (22 hp), which is an ordinary shunt into a wall,
 * so a car the player was still driving shed a wheel and kept going on three
 * of them at full ride height. Owner bug report, and they were right.
 *
 * The ORIGINAL reasoning was correct — for the DRIVING case, which is exactly
 * where it now lives: {@link crashTearable} may not take a wheel, because a
 * car that loses one mid-drive is a physics problem this project has no model
 * for. {@link blastTearable} may, because a wreck does not move. Neither fact
 * overrides the other; they simply belong to different paths.
 *
 * `glass` is in neither because it is not thrown — it is hidden
 * ({@link GLASS}). `lamps` and `body` are in neither because `body` IS the car.
 */

/**
 * THE COLLISION SET: `hood` and the four doors. A panel a crash can knock off
 * and still leave a car that drives.
 *
 * @param {string} name
 * @returns {boolean}
 */
const crashTearable = (name) => typeof name === 'string'
  && (name === 'hood' || name.startsWith('door_'));

/**
 * THE BLAST SET: everything a crash can take, PLUS the four wheels. What a
 * detonation throws — the collision set is a strict SUBSET, so a car that
 * already lost a door in a crash loses the remaining eight when it goes up.
 *
 * What a wreck ALSO needs, once its wheels are gone, is to stop floating at
 * ride height; see `rideHeightOf`. That drop is wreck-only for the same reason
 * this set is: a car that lost a door in a crash still has its wheels under it.
 *
 * @param {string} name
 * @returns {boolean}
 */
const blastTearable = (name) => crashTearable(name)
  || (typeof name === 'string' && name.startsWith('wheel_'));

/**
 * The panel a wreck must LOSE without throwing: the windscreen and the side
 * glass. Blown out, not shattered — there is no shard geometry in this project
 * and inventing some is a much larger job than the read is worth, so the glass
 * simply stops existing (`CarFleet#setPartHidden`). A burnt-out shell with
 * intact windows is the single thing that most breaks "this car is dead".
 */
const GLASS = 'glass';

/**
 * The settle ROLL for a torn panel, by KIND. See {@link settleAttitude} for the
 * Euler-composition reasoning; this is only the axis and the sign.
 *
 * • **hood** — authored in the car's X-Z plane; zero already is flat.
 * • **door** — authored thin in Z. `−π/2` maps local +Z to world +Y, and
 *   `carBuild.js` puts a RIGHT door's outer skin at +Z local and a LEFT door's
 *   at −Z, so the sign is what lands each one PAINT-UP.
 * • **wheel** — `emitWheel` builds it as `cylGeo(..., 'z')`, i.e. a cylinder
 *   about local Z, so at rest its axle is horizontal and it stands like a
 *   wheel. Laying it flat on its face is the SAME rotation as a door's, and
 *   `−π/2` puts local +Z up — which is the hub, the spokes and the cap
 *   (authored at `+w*0.16`, `+w*0.52`, `+w*0.54`). A wheel therefore lands
 *   dish-up rather than on its rim.
 *
 * @param {string} name @returns {number} Radians.
 */
const settleRollOf = (name) => {
  if (name === 'hood') return 0;
  if (name.startsWith('wheel_')) return -HALF_PI;
  return name.endsWith('r') ? -HALF_PI : HALF_PI;
};

/**
 * The car damage model.
 *
 * @param {Object} [o]
 * @param {any} [o.traffic] `Traffic`, or null. Read-only: we walk its driving
 *   ring and its parked pool and never write a field.
 * @param {any} [o.driving] `createDriving`'s system, or null. Read-only.
 * @param {any} [o.fleet] `CarFleet`, or null. Null is a fully-simulating world
 *   with no panels drawn and no wreck tint — see the header.
 * @param {any} [o.fx] The `VehicleFxBatch` (`reset`/`addSmoke`/`addFire`/
 *   `commit`), or null.
 * @param {any} [o.manager] `EnemyManager`, for blast damage to actors and for
 *   the ones a moving car goes through. `damage(i, amt, kx, kz, src)` and the
 *   `x`/`y`/`z`/`radius`/`state` arrays are the whole surface used.
 * @param {any} [o.bus] `'car:wrecked'`, `'car:exploded'`, `'car:ranover'` and,
 *   one minute later, `'car:despawn'`. Null is legal and is what every dev
 *   scene and every node test runs on: a world with nobody listening simply
 *   KEEPS its wrecks, which is correct — nothing is there to remove them.
 *
 *   `'car:ranover'` carries `{x, y, z, dx, dz, victim, byPlayer, fatal}`: the
 *   VICTIM's position at impact, the car's unit travel direction, the manager
 *   slot (or −1 for the player), whether the player was driving, and whether
 *   the hit killed. It fires ONCE per victim per contact and this system is its
 *   sole publisher. The blood belongs to the subscriber — this file paints
 *   nothing, exactly as it removes no car.
 * @param {any} [o.factions] The faction table, for `factions.PLAYER` — the
 *   attacker id that bills a blast kill as the player's crime (see
 *   `docs/factions.md`). Null means every blast is anonymous (`src` −1) and
 *   nothing the player blows up is a crime, which is what every dev scene wants.
 * @param {(x: number, z: number) => number} [o.heightAt] Ground, for the panels.
 * @param {((amount: number, lethal?: boolean) => void)|null} [o.hurtPlayer] Blast
 *   damage to the player.
 *
 *   ⚠ **`lethal` IS ABOUT ARMOUR, NOT ABOUT MAGNITUDE.** `Player#hurt` spends
 *   armour before health, so the 140 of a fireball the player was SITTING IN
 *   is absorbed 100 by a $120 gunsmith's vest and they step out of their own
 *   explosion at 60 hp. The owner's requirement is that being in the car when
 *   it goes up kills you, and NO constant in {@link VEHICLE} can enforce that:
 *   this table has no business knowing what a vest is worth, and whatever
 *   number it picked would be defeated by the next rise in `maxArmour`. So the
 *   distinction rides the SEAM instead:
 *
 *   • `false` (the default) — ordinary damage; armour absorbs it. The blast
 *     RADIUS branch uses this and must keep using it: surviving a nearby
 *     explosion because you were wearing a vest is precisely what the vest is
 *     for, and it is the only thing `Shops`' armour has to spend itself on.
 *   • `true` — strips the armour, then takes the health, so it cannot be
 *     survived at any `maxArmour`. **TWO callers, and they are the two ways a
 *     CAR kills you rather than a bullet:** the player was inside the car that
 *     exploded, and the player was under a car that hit them. ⚠ It was one
 *     caller until the run-over pass existed, and `docs/vehicles.md` /
 *     `CLAUDE.md` still say "exactly ONE"; that line needs correcting. The
 *     test suite pins the COUNT (section (j)), so a third one cannot arrive
 *     silently — and a third would want justifying, because every other route
 *     to the player's health is something a vest is supposed to help with.
 *
 *   Do not "simplify" this back to a single argument. The two calls differ in
 *   KIND, and a version that differed only in amount is the bug this replaced.
 * @param {(() => {x: number, y: number, z: number}|null)|null} [o.playerPos]
 * @param {(() => boolean)|null} [o.playerInCar]
 * @param {string|number} [o.seed='vehicles'] Seeds the panel scatter and the
 *   puff drift, through `streamRng(seed, 'vehicles')` — its OWN stream, so a
 *   crash can never perturb an aim roll or the population's draws (rule 4).
 * @returns {Object}
 */
export function createVehicleDamage(o = {}) {
  let traffic = o.traffic ?? null;
  let driving = o.driving ?? null;
  let fleet = o.fleet ?? null;
  let fx = o.fx ?? null;
  let manager = o.manager ?? null;
  let bus = o.bus ?? null;
  let factions = o.factions ?? null;
  const heightAt = o.heightAt ?? (() => 0);
  const hurtPlayer = o.hurtPlayer ?? null;
  const playerPos = o.playerPos ?? null;
  const playerInCar = o.playerInCar ?? null;
  const rng = streamRng(o.seed ?? 'vehicles', 'vehicles');

  /**
   * One tracked car.
   *
   * @typedef {Object} CarRec
   * @property {number} handle Fleet handle. The map key.
   * @property {number} serial `fleet.serialOf(handle)` when we last looked; the
   *   other half of the identity. −1 when there is no fleet to ask.
   * @property {number} hp @property {number} maxHp
   * @property {number} x @property {number} y @property {number} z
   * @property {number} yaw
   * @property {number} px @property {number} pz The pose the PREVIOUS reconcile
   *   recorded. Differenced against `(x, z)` over `dt` this is the car's
   *   velocity — and it is the ONLY velocity this system has, which is the
   *   point: an AI car is an arc length on a lane, the player's is an
   *   integrated body and the coasting one is a third thing, and none of them
   *   publishes a speed in a common shape. A pose delta is one rule for all
   *   three, which is what the poll-and-reconcile design is FOR.
   * @property {boolean} hasPrev `(px, pz)` is the pose from the IMMEDIATELY
   *   preceding step, and may therefore be differenced.
   *
   *   ⚠ **FALSE ON A FRESH RECORD, AND THAT CLOSES THE RECYCLED-HANDLE TRAP.**
   *   A handle freed and handed straight back out is a DIFFERENT car
   *   (see the header), very possibly on the other side of the island — so
   *   differencing the new car's pose against the old car's would report a
   *   saloon travelling at several hundred m/s and machine-gun every pedestrian
   *   on the line between them. A car has no speed until it has been seen
   *   TWICE, as itself.
   *
   *   ⚠ It is also false after a car BLINKS out of the polls for a step: `px`
   *   would then be two steps old and the velocity would read DOUBLE, which at
   *   a speed gate is the difference between a crawl and a kill.
   * @property {boolean} driven The player is in this car, or rolling it. SET
   *   FRESH EVERY STEP — see the reconcile, and see why it is not `blame`.
   * @property {Map<number, number>} hits Victim slot (or `−1`, the player) →
   *   the reconcile stamp of the last step this car was in contact with them.
   *   An EDGE detector: one hit per contact, not one hit per frame of overlap.
   * @property {number} L @property {number} W Chassis, for the bonnet point.
   * @property {boolean} wreck It has already exploded.
   * @property {number} wreckT Seconds since it did.
   * @property {boolean} despawnAsked `'car:despawn'` has been emitted for this
   *   wreck. A ONE-SHOT latch, and the record outlives it — see the emit site.
   * @property {boolean} blame The PLAYER did this. Sticky: whoever started the
   *   fire owns the explosion, so a car the player shot into flames and walked
   *   away from still bills its blast kills to them.
   * @property {number} smokeAcc @property {number} fireAcc Emission phase
   *   accumulators — puffs are a rate, not a per-frame die, so nothing here
   *   draws rng at a frequency the frame rate could change.
   * @property {number} tick The reconcile pass that last saw it.
   * @property {Set<string>} torn Panel names already off.
   */

  /** @type {Map<number, CarRec>} */
  const cars = new Map();
  /** Reconcile stamp. Anything not carrying the current one is gone. */
  let tick = 0;

  // ── the blast queue (see the header: a QUEUE, never recursion) ─────────────
  /** @type {Array<{x: number, y: number, z: number, handle: number, byPlayer: boolean, depth: number}>} */
  const queue = [];
  /** Read cursor. The queue is drained and then truncated, so nothing shifts. */
  let qHead = 0;
  /** True while {@link drainBlasts} is running — re-entrancy guard. */
  let draining = false;
  /** True for the whole of {@link update} — see {@link explode}. */
  let stepping = false;
  let blastCount = 0;

  // ── panels: a ring buffer of ballistic free bodies ─────────────────────────
  const PART_CAP = VEHICLE.maxParts;
  /** Owning fleet handle, or −1 for a free slot. */
  const qCar = new Int32Array(PART_CAP).fill(-1);
  /** The owner's serial at launch — a recycled handle must not re-adopt an old
   *  panel and start pushing poses at somebody else's bodywork. */
  const qSerial = new Int32Array(PART_CAP).fill(-1);
  /** Part NAME. A fixed-length array of strings, written in place; the fleet
   *  accepts a name or an index and a name survives a re-bake. @type {string[]} */
  const qName = new Array(PART_CAP).fill('');
  const qX = new Float32Array(PART_CAP);
  const qY = new Float32Array(PART_CAP);
  const qZ = new Float32Array(PART_CAP);
  const qVX = new Float32Array(PART_CAP);
  const qVY = new Float32Array(PART_CAP);
  const qVZ = new Float32Array(PART_CAP);
  const qRX = new Float32Array(PART_CAP);
  const qRY = new Float32Array(PART_CAP);
  const qRZ = new Float32Array(PART_CAP);
  const qSX = new Float32Array(PART_CAP);
  const qSY = new Float32Array(PART_CAP);
  const qSZ = new Float32Array(PART_CAP);
  /** 0 = flying, 1 = landed and finished. A landed panel is written ONCE more
   *  and then never again — a wreck is forever, so a panel that keeps being
   *  pushed is a cost that grows every time the player crashes. */
  const qRest = new Uint8Array(PART_CAP);
  /** The attitude and the height this panel settles at, both DERIVED from its
   *  own geometry at tear time (see {@link settleFor}) rather than looked up per
   *  step — a settle is one frame, a fall is sixty. */
  const qSetRX = new Float32Array(PART_CAP);
  const qLift = new Float32Array(PART_CAP);
  let qNext = 0;
  let qLive = 0;

  // ── particles: the same ring, for the same reason ──────────────────────────
  const PCAP = VEHICLE.maxParticles;
  const cX = new Float32Array(PCAP);
  const cY = new Float32Array(PCAP);
  const cZ = new Float32Array(PCAP);
  const cVX = new Float32Array(PCAP);
  const cVY = new Float32Array(PCAP);
  const cVZ = new Float32Array(PCAP);
  const cAge = new Float32Array(PCAP);
  const cLife = new Float32Array(PCAP);
  const cR0 = new Float32Array(PCAP);
  const cGrow = new Float32Array(PCAP);
  /** Grey for smoke, heat for fire — one channel, two meanings, because a
   *  particle is only ever one kind. */
  const cTone = new Float32Array(PCAP);
  const cKind = new Uint8Array(PCAP);
  const cAlive = new Uint8Array(PCAP);
  let cNext = 0;
  let cLive = 0;

  let disposed = false;

  // ─────────────────────────────────────────────────────────────────────────
  // 1. reconcile
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * The fleet's word on a handle, and the ONE place that asks.
   *
   * @param {number} handle
   * @returns {number} The serial; **0** when the fleet confirms the handle
   *   holds no car, and **−1** when there is nobody to ask (no fleet, or one
   *   without the method). −1 is not "dead" — it is "unknown", and the two
   *   callers treat it differently on purpose.
   */
  const serialNow = (handle) => (fleet && typeof fleet.serialOf === 'function'
    ? (fleet.serialOf(handle) | 0)
    : -1);

  /**
   * The one door into the tracked set. Creates a record for a handle we have
   * not seen, RESETS one whose serial moved, and otherwise just refreshes the
   * pose and the stamp.
   *
   * @param {number} handle @param {number} x @param {number} y @param {number} z
   * @param {number} yaw
   * @param {any} chassis `{L, W}` from `Traffic#archChassis` or `body.chassis`.
   */
  const see = (handle, x, y, z, yaw, chassis) => {
    if (!(handle >= 0)) return;
    const serial = serialNow(handle);
    let rec = cars.get(handle);
    // ⚠ THE RECYCLE CHECK. Without it a fresh car inherits the health, the
    // wreck flag and the torn panels of whatever last held this handle — and
    // because the fleet hands a freed handle straight back out, that is not a
    // rare case, it is what happens every time traffic recycles a slot the
    // player had shot at.
    if (rec && fleet && rec.serial !== serial) {
      forget(rec, false);
      rec = undefined;
    }
    if (!rec) {
      rec = {
        handle,
        serial,
        hp: VEHICLE.maxHp,
        maxHp: VEHICLE.maxHp,
        x,
        y,
        z,
        yaw,
        // ⚠ A CAR HAS NO SPEED UNTIL IT HAS BEEN SEEN TWICE. See `CarRec.hasPrev`
        // — this is the line that stops a recycled handle reading as a
        // teleport. `px`/`pz` are seeded to the current pose rather than left
        // undefined so that a caller who ignores the flag still gets zero
        // rather than NaN.
        px: x,
        pz: z,
        hasPrev: false,
        driven: false,
        hits: new Map(),
        L: chassis?.L ?? 4.2,
        W: chassis?.W ?? 1.8,
        wreck: false,
        wreckT: 0,
        despawnAsked: false,
        /** The windscreen has been blown out and owes a restore to `forget`. */
        glassOut: false,
        /** Metres this shell drops when it becomes a wreck — its own measured
         *  ride height, resolved once at detonation. */
        dropY: 0,
        blame: false,
        smokeAcc: 0,
        fireAcc: 0,
        tick,
        torn: new Set(),
      };
      cars.set(handle, rec);
      return;
    }
    // ── the pose delta, and it is captured ONCE per step ────────────────────
    //
    // Guarded on the stamp rather than run unconditionally, because a handle
    // CAN be reported twice in one reconcile (a car mid-handover is a `Traffic`
    // slot and a `Driving` body for a frame). A second capture in the same step
    // would set `px = x`, i.e. silently zero the car's speed on exactly the
    // frames a carjack happens.
    //
    // ⚠ `tick - 1` and not merely "not this tick": see `CarRec.hasPrev`. A car
    // that missed a step has a pose two steps old, and differencing it reports
    // double the speed.
    if (rec.tick !== tick) {
      rec.hasPrev = rec.tick === tick - 1;
      rec.px = rec.x;
      rec.pz = rec.z;
      // ⚠ NOT `blame`, and never `blame`. See the reconcile's driving block.
      rec.driven = false;
    }
    rec.serial = serial;
    rec.x = x;
    rec.y = y;
    rec.z = z;
    rec.yaw = yaw;
    if (chassis) {
      rec.L = chassis.L ?? rec.L;
      rec.W = chassis.W ?? rec.W;
    }
    rec.tick = tick;
  };

  /**
   * Drops a car and everything that belonged to it.
   *
   * @param {CarRec} rec
   * @param {boolean} [drop=true] Remove it from the map. False when the caller
   *   is about to overwrite the entry anyway (the recycle path).
   */
  const forget = (rec, drop = true) => {
    for (let i = 0; i < PART_CAP; i++) {
      if (qCar[i] !== rec.handle || qSerial[i] !== rec.serial) continue;
      qCar[i] = -1;
      qLive--;
    }
    // Hand the bodywork back. The handle is about to be REUSED by a car that
    // has all its panels, and a fleet that kept the detach flag would draw the
    // new car with a hole where its door should be. Harmless when the car is
    // simply gone.
    if (fleet && typeof fleet.setPartDetached === 'function') {
      for (const name of rec.torn) fleet.setPartDetached(rec.handle, name, false);
    }
    // ...INCLUDING THE GLASS. `release()` is how the review wall rebuilds an
    // exhibit, and a rebuilt car with no windows is a rebuild that did not
    // happen. The `y` the handle's next tenant is placed at is `Traffic`'s to
    // write, so the wreck's drop needs no undoing here.
    if (rec.glassOut && fleet && typeof fleet.setPartHidden === 'function') {
      fleet.setPartHidden(rec.handle, GLASS, false);
    }
    rec.glassOut = false;
    rec.torn.clear();
    if (drop) cars.delete(rec.handle);
  };

  /**
   * Walks all four owners. Nothing below this reads `traffic` or `driving`
   * again, which is what makes one `update` a snapshot rather than a race.
   */
  const reconcile = () => {
    tick++;

    if (traffic) {
      const top = traffic.top | 0;
      for (let i = 0; i < top; i++) {
        if (!traffic.isDriving?.(i)) continue;
        const h = traffic.handle?.[i] ?? -1;
        if (!(h >= 0)) continue;
        see(h, traffic.x[i], traffic.y ? traffic.y[i] : 0, traffic.z[i], traffic.yaw[i],
          traffic.archChassis?.[traffic.arch[i]]);
      }
      const pcap = traffic.parkedCap | 0;
      for (let p = 0; p < pcap; p++) {
        if (!traffic.isParked?.(p)) continue;
        const h = traffic.pHandle?.[p] ?? -1;
        if (!(h >= 0)) continue;
        see(h, traffic.pX[p], traffic.pY ? traffic.pY[p] : 0, traffic.pZ[p], traffic.pYaw[p],
          traffic.archChassis?.[traffic.pArch[p]]);
      }
    }

    if (driving) {
      // `fleetHandle` is >= 0 for the whole entry/exit sequence, not just while
      // `inCar` — a car being climbed into is still a car that can be shot.
      //
      // ⚠⚠ **THIS IS ALSO WHERE `driven` IS SET, AND IT IS NOT `blame`.** The
      // two look interchangeable and are not:
      //
      //   • `blame` is STICKY and about the PAST — whoever started the fire owns
      //     the explosion, so a car the player shot and walked away from still
      //     bills its blast kills to them. That is correct, for a blast.
      //   • `driven` is about the PRESENT and is re-set every step (see `see`).
      //     Who is behind the wheel RIGHT NOW is the only thing that decides
      //     whether the body under the bumper is the player's crime.
      //
      // Reuse `blame` here and this happens: the player rams an AI saloon
      // (blame = true, correctly), the saloon drives on down the street, kills a
      // pedestrian two junctions away, and the player takes the wanted level for
      // it. A carjack changes hands mid-session, in both directions, which is
      // exactly why a sticky answer to "who is driving" is always eventually
      // wrong.
      //
      // The COASTING body counts as the player's doing — they set it rolling,
      // and `Breakables.js` already counts it as theirs for the same reason.
      const h = driving.fleetHandle ?? -1;
      const b = driving.body;
      if (h >= 0 && b) {
        see(h, b.x, b.y ?? 0, b.z, b.yaw, b.chassis);
        const r = cars.get(h);
        if (r) r.driven = true;
      }
      const ch = driving.coastHandle ?? -1;
      const cb = driving.coastBody;
      if (ch >= 0 && cb) {
        see(ch, cb.x, cb.y ?? 0, cb.z, cb.yaw, cb.chassis);
        const r = cars.get(ch);
        if (r) r.driven = true;
      }
    }

    // ── who is GONE ──────────────────────────────────────────────────────────
    //
    // ⚠⚠ **ABSENCE IS NOT AUTHORITATIVE WHEN THERE IS AN AUTHORITY.** This
    // sweep used to forget any record that failed to report in, unconditionally
    // — and the comment already called that "the fallback for when `fleet` is
    // null" while nothing gated it on that. The symptom is specific and nasty:
    // a car that blinks out of BOTH polls for a single step, for any reason,
    // has its record destroyed and is re-minted at `maxHp` on the very next
    // step, with its torn panels handed back. **A car healing itself in the
    // middle of a firefight**, with no page reload and nothing asking for it.
    //
    // The fleet already answers the question properly: `serialOf` returns 0 for
    // a handle that holds no car, and a serial that MOVED is a different car
    // (`see` handles that case). So with a fleet we forget on the fleet's word
    // and never on silence. Every real exit in `Traffic.js` goes through
    // `fleet.removeCar` — `_deactivate`, `_freeParked`, the despawn ring and
    // the teardown — so nothing leaks; `takeDriving`/`takeParked` TRANSFER the
    // handle rather than releasing it, which is exactly why they must not look
    // like an exit here.
    //
    // ⚠ Do not "simplify" this back to the one-line sweep. Without a fleet it
    // IS the one-line sweep; with one, silence means "I did not see it", which
    // is not the same fact as "it is gone".
    for (const rec of cars.values()) {
      if (rec.tick === tick) continue;
      const s = serialNow(rec.handle);
      // No fleet (or a fleet that cannot answer): the absence rule is all we
      // have. It is a whole step slower than the serial check and it cannot
      // tell a blink from a death — which is why the serial check exists.
      if (s < 0) {
        forget(rec);
        continue;
      }
      if (s === 0 || s !== rec.serial) forget(rec);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // 1b. running people over
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * hp a car doing `v` m/s costs whoever it hits. Linear from zero at
   * {@link VEHICLE.runOverSpeed}; see that constant for where the slope comes
   * from and what it says about each member of the cast.
   *
   * @param {number} v m/s. @returns {number} Always > 0 at the call sites,
   *   because they have already cleared the gate.
   */
  const runOverAmount = (v) => (v - VEHICLE.runOverSpeed) * VEHICLE.runOverDamage;

  /**
   * THE SWEEP. Every car the reconcile saw this step, against every live actor
   * and against the player on foot.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * AI CARS DO NOT BRAKE FOR PEOPLE, AND THAT IS THE DESIGN
   * ─────────────────────────────────────────────────────────────────────────
   * `Traffic` brakes for a leader, a junction stop line and the PLAYER as a
   * pedestrian; it does not brake or swerve for the crowd, by owner decision —
   * that is the classic reading of this genre. What was missing was the other
   * half of it: traffic drove THROUGH pedestrians, silently. This connects.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * BRUTE FORCE IS THE MEASURED ANSWER — DO NOT "OPTIMISE" IT
   * ─────────────────────────────────────────────────────────────────────────
   * ~24 cars that are actually MOVING × ~45 live actors ≈ **1,080 squared
   * distances a frame**, and `Breakables.update` already spends ~1,680 a frame
   * over ~840 props without anybody noticing. The speed gate is what makes the
   * first number 24 rather than 200: a large share of the fleet is stopped at
   * any instant (junction claims, headway braking) and a stopped car pays two
   * compares and leaves.
   *
   * ⚠ **A SPATIAL HASH WOULD COST MORE THAN IT SAVED** at this population — it
   * is a rebuild per frame over a set this small — and it is another structure
   * to keep in step with `EnemyManager`'s own.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * THE CONTACT ZONE IS AN ORIENTED BOX IN THE CAR'S OWN FRAME
   * ─────────────────────────────────────────────────────────────────────────
   * ```
   *   |along|  <= rec.L / 2            + r     the car's real chassis LENGTH
   *   |across| <= VEHICLE.runOverReach + r     a bumper, not a body
   *   |ay|     <= VEHICLE.runOverReach + r     the storey test, see below
   * ```
   * where `r` is the victim's own radius, `along`/`across` are the actor
   * projected onto the car's heading and its right vector, and the heading is
   * `(cos yaw, −sin yaw)` — the convention `Traffic#_actorAhead` spells out,
   * with `right = (−fz, fx)`. **The length is READ off the chassis
   * (`Traffic#archChassis` → `CarRec.L`), never tabled here**: this file has no
   * business knowing how long a saloon is, and a second copy would drift the
   * first time an archetype changed.
   *
   * ⚠ **IT WAS A SPHERE ABOUT THE CAR'S ORIGIN AND THE RIG KILLED THAT.** See
   * {@link VEHICLE.runOverReach} for the measurement; the short version is that
   * the front 1.2 m of the car passed through people before anything
   * registered. The sphere's own defence — "the car is moving THROUGH the
   * space, so the circle sweeps the same ground a step or two later" — is TRUE
   * and is not the point: it means the blood lands when the pedestrian is level
   * with the DRIVER, which is a car that has already driven through them.
   *
   * ⚠ It costs about four flops a pair against the ~1,080 pairs above, and it
   * stays absolute compares with an early-out at every stage — the Y test
   * FIRST, because it is the cheapest thing in the loop and it throws out an
   * entire shop crowd before either dot product is formed.
   *
   * ⚠⚠ **AND DO NOT SAMPLE ON ALTERNATE FRAMES.** It is the tempting halving
   * and it silently drops the COMMON case. At 17 m/s a dead-centre hit overlaps
   * for ~11 frames and would survive any amount of frame-skipping; a GLANCING
   * hit — the shoulder-clip, which is most of them — overlaps for one or two,
   * and half of those vanish. The failure mode is "sometimes the car just goes
   * through them", which is indistinguishable from the bug this whole pass
   * exists to fix.
   *
   * @param {number} dt Always > 0 — the caller has already returned otherwise.
   */
  const runOver = (dt) => {
    const inv = 1 / dt;
    const top = manager ? (manager.top | 0) : 0;
    const inCar = playerInCar?.() === true;
    // ⚠ THE PLAYER IS HIT ONLY ON FOOT. A player inside a car is this system's
    // existing crash and blast business, and "THE PLAYER DOES NOT BLEED IN A
    // CAR" is an owner requirement (`docs/vehicles.md`) — rounds hit the car and
    // the car going up is what kills them. It also means a car can never run
    // over its own driver, which needs no separate test.
    //
    // ⚠ THAT LAST SENTENCE IS TRUE OF THE PLAYER AND WAS READ AS TRUE OF
    // EVERYBODY, WHICH IS EXACTLY THE BUG THE `BRAIN.SEATED` GATE BELOW CLOSES.
    // An NPC driver has no `playerInCar` to exempt them: they are an ordinary
    // manager slot whose transform happens to be a seat. See the crowd loop.
    const pp = inCar ? null : (playerPos?.() ?? null);

    for (const rec of cars.values()) {
      // Only cars the reconcile saw THIS step: `CarRec.hasPrev` is about the
      // previous pose being fresh, and this is about the current one being.
      if (rec.tick !== tick || !rec.hasPrev) continue;

      const dx = rec.x - rec.px;
      const dz = rec.z - rec.pz;
      const len = Math.hypot(dx, dz);
      const v = len * inv;
      // ── THE GATE, FIRST, BEFORE ANYTHING IS ALLOCATED OR WALKED ──
      if (v < CRAWL_MIN) {
        // Parked, or as good as. `PARK_CIRCLE` colliders already keep people
        // off it (see `VEHICLE.runOverNudge`), and there is no contact to
        // remember.
        if (rec.hits.size) rec.hits.clear();
        continue;
      }
      const ux = dx / len;
      const uz = dz / len;
      // ── the car's OWN frame, from its yaw and not from its travel ──────────
      //
      // ⚠ `Ry` maps +X to `(cos, −sin)`, which is the convention
      // `Traffic#_actorAhead` writes out and the one every heading in this
      // project uses; `right` is `(−hz, hx)` from the same place. It is the YAW
      // and not the pose delta on purpose: `(ux, uz)` is where the car is
      // GOING, which for a car in reverse or sliding a corner is not the axis
      // its bodywork lies along, and the bodywork is what hits people. The
      // travel direction is still what the knockback and the payload carry —
      // you are thrown the way the car was moving.
      const hx = Math.cos(rec.yaw);
      const hz = -Math.sin(rec.yaw);
      const halfL = rec.L * 0.5;
      const kill = v >= VEHICLE.runOverSpeed;
      // ⚠⚠ **NOTHING CLEARS `rec.hits` HERE, AND THAT IS THE FIX FOR A MEASURED
      // BUG.** It used to read `if (!kill && rec.hits.size) rec.hits.clear()`,
      // which looks like tidy housekeeping and is a re-arming of the
      // one-hit-per-contact edge detector. `v` is a pose delta off a float32
      // grid, so a car sitting ON the gate flips either side of it from step to
      // step — and every `false` step wiped the contact set, so the next `true`
      // step billed the same body again. `/play/cardamage` measured ONE 6.6 s
      // pass billing **504 hits** and emitting 504 `'car:ranover'`, i.e. 504
      // splashes of blood, each worth about five thousandths of an hp. The band
      // is narrow; a car braking to a halt through a crowd passes through it
      // every single time.
      //
      // A contact now expires in exactly ONE place — the `t !== tick` sweep at
      // the bottom of this loop — so it survives any number of sub-gate steps,
      // and BOTH sub-gate paths below stamp it (the crowd through `nudge`, the
      // player through the overlap test) so a body being shouldered aside is
      // still "in contact" and cannot be re-billed the instant `v` ticks back
      // over. No hysteresis band was needed: the tick bookkeeping closes it on
      // its own, and a second speed threshold to keep the first one honest is
      // the two-knobs-for-one-job bug this project documents against.
      const amt = kill ? runOverAmount(v) : 0;
      // ⚠⚠ **THE HIT-AND-RUN FIX IS THIS ONE ARGUMENT, AND IT ADDS NO BRANCH TO
      // THE CRIME TAXONOMY.** `attachFactionCombat` (`Population.js`) already
      // gates on `src !== factions.PLAYER` in `onDamage` and on
      // `lastAttacker === factions.PLAYER` in `onKill`, so an AI saloon that
      // flattens a pedestrian passes −1, never reaches `crime()`, and cannot
      // raise a wanted level — while the identical collision under the player's
      // own bumper does. Nothing new is taught to the taxonomy, nothing new is
      // emitted, and `srcOf` is REUSED rather than a second attribution rule
      // being written next to the first (see `docs/factions.md`: only the
      // player's actions are crimes).
      //
      // ⚠ The verb the taxonomy then prints is `shot_civilian`, because `shot`
      // and `killed` are the only two it has. That is a naming wart in the
      // existing taxonomy and NOT a reason to grow a third verb here — a
      // `ranover_civilian` would need a side in the wanted table, a line in the
      // dialogue banks and a reason to differ from `shot_`, and it has none.
      const src = kill ? srcOf(rec.driven) : -1;

      // ── the crowd ──
      for (let i = 0; i < top; i++) {
        if (!manager.isLive?.(i)) continue;
        const st = manager.state?.[i];
        if (st === STATE.DEAD || st === STATE.DYING) continue;
        // ⚠⚠ **A SEATED ACTOR IS NOT STANDING IN THE ROAD — IT IS THE CAR.**
        // `BRAIN.SEATED` means the slot's transform is SLAVED to somebody
        // else's (`Population.js` writes it from `Traffic#getSeatWorld` every
        // step, `Carjack` from the drag), so its x/y/z is not a body's position
        // at all — it is a SEAT's. Every occupied car therefore had its own
        // driver sitting dead centre of its own contact box, permanently, and
        // billed them the ramp on the first step it cleared the gate: a
        // civilian (30 hp) dies at 6.4 m/s, which is every car in the city
        // within a second of the level starting. The same box reached into the
        // NEXT car along too, so a shunt killed the other driver. Both readings
        // were geometrically correct over a position that means something else.
        //
        // ⚠ The skip is EVERY seated slot, not just this car's own occupants.
        // Whose seat it is makes no difference to the argument: a body in a car
        // is protected BY the car — `docs/vehicles.md`'s "THE PLAYER DOES NOT
        // BLEED IN A CAR", stated for everybody else. Ramming a car damages the
        // CAR, and the blast that follows is already 3D, already reaches the
        // cabin and already kills whoever is in it; `resolveBlast` is where a
        // car killing its passengers belongs, and it needs no change here.
        //
        // ⚠ It is the BRAIN and not `inCabin`, which also covers the carjack
        // drag: `inCabin` goes false the moment the victim's body is outside
        // the door (`EnemyManager#placeSeated`), but they stay SEATED for the
        // whole drag and their pose is still `Carjack`'s to write, not theirs.
        //
        // Same family as `EnemyManager#_acquire`'s "NOBODY HUNTS A CAR" and
        // `Speech.js`'s "a seated slot never speaks": three systems, one rule —
        // a SEATED slot is furniture, and reading its position as a person's
        // standing place is the bug each of them had to close.
        if (manager.brain?.[i] === BRAIN.SEATED) continue;
        const r = manager.radius ? manager.radius[i] : ACTOR_RADIUS;
        const halfW = VEHICLE.runOverReach + r;
        // ⚠ THREE DIMENSIONS, exactly as `resolveBlast` is 3D and for exactly
        // the same reason: a shop crowd stands 100 m under the city
        // (`docs/interiors.md`), and an XZ test would have every car on the
        // street above driving through a grocer's customers. It is FIRST
        // because it is the cheapest reject in the loop — one subtract and two
        // compares, before either dot product exists — and because the crowd it
        // rejects is the one this loop can never legitimately reach. The box's
        // vertical half-extent reuses the lateral one: the question it answers
        // is "same storey", and inventing a third number for it would be a
        // constant nobody could see.
        const ay = (manager.y ? manager.y[i] : rec.y) - rec.y;
        if (ay > halfW || ay < -halfW) continue;
        const ax = manager.x[i] - rec.x;
        const az = manager.z[i] - rec.z;
        // ...then ALONG the car, which is the dimension the sphere got wrong,
        // and only then ACROSS it. See the header for the frame and for why the
        // length comes off `rec.L` rather than out of a table.
        const along = ax * hx + az * hz;
        const halfLr = halfL + r;
        if (along > halfLr || along < -halfLr) continue;
        const across = ax * -hz + az * hx;
        if (across > halfW || across < -halfW) continue;

        if (!kill) {
          // ⚠ THE STAMP IS PART OF THE NUDGE, not bookkeeping around it: a body
          // being shouldered aside is IN CONTACT, and a contact that is not
          // recorded is a contact the next over-gate step bills as fresh. See
          // the `rec.hits` note above.
          rec.hits.set(i, tick);
          nudge(i, across, halfW, -hz, hx, dt);
          continue;
        }
        // ONE hit per CONTACT, not one per frame of overlap: at 60 Hz a 17 m/s
        // car overlaps a body for ~11 steps, and billing all eleven is eleven
        // times the damage and eleven splashes of blood on the road.
        const last = rec.hits.get(i);
        rec.hits.set(i, tick);
        if (last === tick - 1) continue;

        const vx = manager.x[i];
        const vy = manager.y ? manager.y[i] : rec.y;
        const vz = manager.z[i];
        // ⚠ `manager.damage`'s knockback IS the push. There is no second shove
        // here and there must not be one — a body that took a car's damage and
        // then a separate displacement is two systems moving the same actor.
        const fatal = manager.damage?.(i, amt, ux, uz, src) === true;
        // ⚠ SOLE PUBLISHER of this channel, and the BLOOD IS NOT OUR JOB: this
        // file imports no three.js, so it says what happened and `Gameplay`
        // paints it — the same separation as `'car:wrecked'` and
        // `'car:despawn'` (see the header).
        bus?.emit?.('car:ranover', {
          x: vx,
          y: vy,
          z: vz,
          dx: ux,
          dz: uz,
          victim: i,
          byPlayer: rec.driven,
          fatal,
        });
      }

      // ── the player, on foot ──
      //
      // ⚠ THERE IS NO NUDGE BRANCH HERE AND IT IS NOT AN OVERSIGHT: a shove
      // needs somewhere to write, and the only player seams this system is
      // handed are `playerPos` (a READ, and the live `Player#position` object —
      // writing through it would be reaching into `Player`, which this file may
      // not do) and `hurtPlayer`. So a slow car passes through the player
      // bloodlessly, which is a smaller wrong than a damage model that mutates
      // an actor it was only lent a view of. The missing seam is a shove — a
      // `pushPlayer(dx, dz, speed)` closure alongside `hurtPlayer`.
      //
      // ⚠ THE OVERLAP IS TESTED WHETHER OR NOT THE CAR IS OVER THE GATE, AND
      // THE CONTACT IS STAMPED EITHER WAY. The crowd gets that for free from
      // the nudge; the player has no nudge (see above), so without this the
      // player is the one victim the gate chatter could still re-bill every
      // time `v` crossed back over the threshold. The cost is that a car which
      // crawled onto the player and then accelerated does not bill that
      // contact — which is the same rule the crowd already lives under, and the
      // car is moving, so the contact ends within a second either way.
      if (pp) {
        const py = (pp.y ?? rec.y) - rec.y;
        const pHalfW = VEHICLE.runOverReach + PLAYER_RADIUS;
        const px = pp.x - rec.x;
        const pz = pp.z - rec.z;
        const pAlong = px * hx + pz * hz;
        const pAcross = px * -hz + pz * hx;
        const pHalfL = halfL + PLAYER_RADIUS;
        if (py <= pHalfW && py >= -pHalfW
          && pAlong <= pHalfL && pAlong >= -pHalfL
          && pAcross <= pHalfW && pAcross >= -pHalfW) {
          const last = rec.hits.get(VICTIM_PLAYER);
          rec.hits.set(VICTIM_PLAYER, tick);
          if (kill && last !== tick - 1) {
            // ⚠ `lethal: true`, and this is the SECOND caller of that flag (the
            // first is the player sitting in a car that goes up). Same argument,
            // different event: the flag is about ARMOUR, not about magnitude,
            // and a $120 gunsmith's vest must not absorb a saloon. A car is not
            // a bullet, and no constant in `VEHICLE` could enforce that — the
            // next rise in `maxArmour` would defeat it.
            hurtPlayer?.(amt, true);
            bus?.emit?.('car:ranover', {
              x: pp.x,
              y: pp.y ?? rec.y,
              z: pp.z,
              dx: ux,
              dz: uz,
              victim: VICTIM_PLAYER,
              byPlayer: rec.driven,
              // ⚠ ALWAYS FALSE for the player, and honestly so. `fatal` means
              // "this hit killed", and the only thing that could answer that is
              // `Player#dead` — which is not injected here and must not be, for
              // the same reason `Weapons.js` is not given the `Player`: a second
              // system deciding whether the player is alive is the
              // `hurtPlayer(amount, lethal)` family of bug. The subscriber
              // already knows the player's health; we do not.
              fatal: false,
            });
          }
        }
      }

      // Contacts that ENDED this step. The map is the CURRENT contact set and
      // nothing else, so it can never grow with the number of people a car has
      // driven past over a session.
      for (const [k, t] of rec.hits) if (t !== tick) rec.hits.delete(k);
    }
  };

  /**
   * A body a SLOW car is standing in: displaced out of the footprint, no damage
   * and no event. See {@link VEHICLE.runOverNudge} for why this exists at all
   * and why a parked car does not need it.
   *
   * It writes the actor's POSITION rather than its velocity, deliberately: the
   * pedestrian step recomputes `vx/vz` from its goal every frame, so a velocity
   * written here is gone before it moves anybody, whereas a position the
   * manager integrates FROM is respected. It is capped per step, so it is a
   * shove and never a teleport, and it converges — once `|across|` clears the
   * box's half-width there is nothing left to do.
   *
   * ⚠⚠ **THE PUSH IS ON THE CAR'S LATERAL AXIS, AND THAT IS A FIX FOR A
   * MEASURED BUG.** It used to push along `actor − car origin` and call that
   * "outward from the car, which reads as being shouldered aside". It does not:
   * for the case that actually happens — a body in FRONT of the bumper — that
   * vector is dominated by the ALONG-TRACK component, so the shove points down
   * the road and the car HERDS the crowd ahead of itself like a snowplough for
   * as long as it keeps crawling. `/play/cardamage`, checklist R3. Pushing on
   * the lateral axis is what makes a crawling car PART a crowd, and it is also
   * the only direction that converges: the box is unbounded in neither axis,
   * but the car keeps arriving along the other one.
   *
   * A body dead on the centre line has no side to be on, so it goes to the
   * car's RIGHT — the same fallback `resolveBlast` makes for a body at the
   * exact centre of a blast, and for the same reason: never a push along
   * (0, 0).
   *
   * @param {number} i Actor slot.
   * @param {number} across The actor's signed offset on the car's right axis.
   * @param {number} halfW The box's lateral half-width: bumper + the actor's
   *   own radius. Clearing it is the whole exit condition.
   * @param {number} rx @param {number} rz The car's unit RIGHT vector,
   *   `(−hz, hx)` off its heading — the caller already has it.
   * @param {number} dt
   */
  const nudge = (i, across, halfW, rx, rz, dt) => {
    if (!manager?.x || !manager.z) return;
    const side = across < -1e-4 ? -1 : 1;
    const need = halfW - Math.abs(across);
    const step = Math.min(need, VEHICLE.runOverNudge * dt);
    if (!(step > 0)) return;
    manager.x[i] += rx * side * step;
    manager.z[i] += rz * side * step;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // panels
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * How a panel comes to rest, DERIVED from the part's own authored geometry.
   *
   * ⚠ This replaced two tabled constants (`VEHICLE.hoodLift` / `doorLift`),
   * whose own docblock said they existed only because `CarFleet` published no
   * geometry query. It does now, so they are gone — and they were WRONG: a
   * sedan's hood needs 0.111 m against the tabled 0.03 and its doors 0.115
   * against 0.06, i.e. every settled panel was sunk 5–8 cm into the tarmac.
   *
   * The lift is "the lowest point of the rotated geometry touches the ground",
   * and with `ry = 0` the settle rotation reduces to one axis:
   *
   * ```
   *   rx = 0      worldY = local Y   → lift = −minY   (a hood)
   *   rx = −π/2   worldY = +local Z  → lift = −minZ   (right door, any wheel)
   *   rx = +π/2   worldY = −local Z  → lift = +maxZ   (left door)
   * ```
   *
   * NOT clamped at zero: a part whose geometry sits entirely above its own
   * origin wants a NEGATIVE lift, i.e. the origin buried, and clamping would
   * float it instead. The origin is not drawn.
   *
   * @param {number} handle @param {string} name
   * @returns {{rx: number, lift: number}} `lift` 0 when the fleet cannot answer
   *   — a fleet that publishes parts but not extents is one mid-upgrade, and
   *   resting on the origin is the least-wrong thing available.
   */
  const settleFor = (handle, name) => {
    const rx = settleRollOf(name);
    const e = fleet && typeof fleet.partExtentOf === 'function' ? fleet.partExtentOf(handle, name) : null;
    if (!e) return { rx, lift: 0 };
    return { rx, lift: rx === 0 ? -e.minY : rx < 0 ? -e.minZ : e.maxZ };
  };

  /**
   * THE RIDE HEIGHT: how far the shell floats above the road, and therefore
   * exactly how far a wreck has to come DOWN once its wheels are off.
   *
   * Car space puts `y = 0` on the tarmac (a wheel's rest is `y = wheelR`), so
   * the lowest point of the body over the NON-WHEEL parts IS the gap. Measured,
   * never tabled: it is a per-archetype number and a van is not a hatchback.
   *
   * ⚠ Rest ROTATIONS are ignored, and that is safe rather than sloppy: only the
   * cabin parts carry one (the raked steering column), and the minimum always
   * comes from the body's floor pan, which is authored square. A rotated part
   * cannot become the minimum without the shell having a hole in its floor.
   *
   * @param {number} handle
   * @returns {number} Metres, 0 when the fleet cannot answer.
   */
  const rideHeightOf = (handle) => {
    if (!fleet || typeof fleet.partExtentOf !== 'function' || typeof fleet.partRestOf !== 'function') return 0;
    const names = typeof fleet.partsOf === 'function' ? fleet.partsOf(handle) : null;
    if (!Array.isArray(names)) return 0;
    let lo = Infinity;
    for (let i = 0; i < names.length; i++) {
      const n = names[i];
      if (typeof n !== 'string' || n.startsWith('wheel_')) continue;
      const e = fleet.partExtentOf(handle, n);
      const r = fleet.partRestOf(handle, n);
      if (!e || !r) continue;
      const y = (r.y ?? 0) + e.minY;
      if (y < lo) lo = y;
    }
    return Number.isFinite(lo) && lo > 0 ? lo : 0;
  };

  /**
   * Sits a wreck down on its floor pan.
   *
   * ⚠ **IDEMPOTENT BY CONSTRUCTION, not by a flag.** It always computes from
   * the SOURCE pose the reconcile recorded (`rec.y`, which is `Traffic`'s or
   * `Driving`'s own number) and never from its own previous output, so applying
   * it twice in one step, or every step for a minute, yields the identical
   * transform. That is what makes re-asserting it safe — and re-asserting is
   * necessary, because `Traffic.js` rewrites a DRIVING car's transform every
   * frame (line ~1732) and would otherwise lift the shell straight back up
   * during the window between `car:wrecked` and the wiring retiring the slot.
   * A PARKED wreck is written once, on placement (`placeParked` /
   * `returnParked`), so for that case one write would do — this covers both
   * without having to know which it is.
   *
   * @param {CarRec} rec
   */
  const applyWreckDrop = (rec) => {
    if (!rec.wreck || !(rec.dropY > 0)) return;
    if (!fleet || typeof fleet.setCarTransform !== 'function') return;
    fleet.setCarTransform(rec.handle, rec.x, rec.y - rec.dropY, rec.z, rec.yaw);
  };

  /**
   * Claims a ring slot and launches a panel from it.
   *
   * @param {CarRec} rec @param {string} name
   * @param {number} vx @param {number} vy @param {number} vz
   */
  const launchPart = (rec, name, vx, vy, vz) => {
    const i = qNext;
    qNext = (qNext + 1) % PART_CAP;
    // Overwriting a live slot does NOT hand that panel back: it is lying in the
    // road, the fleet is still drawing it there, and un-detaching it would make
    // a door reappear on a wreck two streets away. It simply stops being
    // simulated, which for a panel that has already settled is no change at all.
    if (qCar[i] >= 0) qLive--;
    qCar[i] = rec.handle;
    qSerial[i] = rec.serial;
    qName[i] = name;
    // Panels leave from roughly bonnet height at the car's centre. We do not
    // know where a part sits in its own body — `partsOf` publishes names, not
    // transforms — and at 52° over a 4 m car the difference is under a metre
    // for the one frame before the arc takes over.
    qX[i] = rec.x;
    qY[i] = rec.y + 0.75;
    qZ[i] = rec.z;
    qVX[i] = vx;
    qVY[i] = vy;
    qVZ[i] = vz;
    qRX[i] = 0;
    qRY[i] = rec.yaw;
    qRZ[i] = 0;
    qSX[i] = (rng() * 2 - 1) * VEHICLE.partSpin;
    qSY[i] = (rng() * 2 - 1) * VEHICLE.partSpin;
    qSZ[i] = (rng() * 2 - 1) * VEHICLE.partSpin;
    const s = settleFor(rec.handle, name);
    qSetRX[i] = s.rx;
    qLift[i] = s.lift;
    qRest[i] = 0;
    qLive++;
    rec.torn.add(name);
    if (fleet && typeof fleet.setPartDetached === 'function') fleet.setPartDetached(rec.handle, name, true);
  };

  /**
   * ONE walk of the part list, so the two paths cannot drift: the CALLER hands
   * in which set it means ({@link crashTearable} or {@link blastTearable}) and
   * the "skip what is already torn" rule is written once.
   *
   * @param {CarRec} rec
   * @param {(name: string) => boolean} pred Which tearable set.
   * @returns {string[]} Panels still attached that could come off. Empty for a
   *   fleet-less world and for an archetype with no doors — both are legal, and
   *   both simply mean nothing tears.
   */
  const tearablesOf = (rec, pred) => {
    if (!fleet || typeof fleet.partsOf !== 'function') return [];
    const all = fleet.partsOf(rec.handle);
    if (!Array.isArray(all)) return [];
    const out = [];
    for (let i = 0; i < all.length; i++) {
      if (pred(all[i]) && !rec.torn.has(all[i])) out.push(all[i]);
    }
    return out;
  };

  /**
   * ONE panel off a big hit, thrown the way the impact was going — from the
   * COLLISION set only. The car is very probably still being driven.
   *
   * @param {CarRec} rec @param {number} kx @param {number} kz
   */
  const tearOne = (rec, kx, kz) => {
    const opts = tearablesOf(rec, crashTearable);
    if (!opts.length) return;
    const pick = opts[Math.min(opts.length - 1, (rng() * opts.length) | 0)];
    const len = Math.hypot(kx, kz);
    // A zero impact direction is legal (a wall hit reports no direction), and
    // a panel launched along (0,0) would land back on the car it came off. Fall
    // back to the car's own heading.
    const fx = len > 1e-4 ? kx / len : Math.cos(rec.yaw);
    const fz = len > 1e-4 ? kz / len : -Math.sin(rec.yaw);
    const push = VEHICLE.partToss * (0.7 + rng() * 0.6);
    launchPart(rec, pick, fx * push, VEHICLE.partToss * (0.35 + rng() * 0.4), fz * push);
  };

  /**
   * EVERY remaining panel of the BLAST set, thrown outward from the blast —
   * the wheels included, which is what stops a burnt-out shell sitting on four
   * intact tyres.
   *
   * @param {CarRec} rec
   */
  const tearAll = (rec) => {
    const opts = tearablesOf(rec, blastTearable);
    for (let i = 0; i < opts.length; i++) {
      const a = rng() * Math.PI * 2;
      const push = VEHICLE.blastToss * (0.55 + rng() * 0.7);
      launchPart(rec, opts[i], Math.cos(a) * push, VEHICLE.blastToss * (0.5 + rng() * 0.6), Math.sin(a) * push);
    }
  };

  /**
   * Lays panel `i` down FLAT — and the attitude is per part KIND, not one
   * constant, because the two kinds are built in different planes.
   *
   * `CarFleet#setPartPose` writes `T · R_xyz`, i.e. three.js Euler order 'XYZ',
   * which composes as **R = Rx · Ry · Rz**. That composition order is what
   * decides which channel may carry the panel's spin on the ground, and it is
   * not interchangeable:
   *
   * • **A HOOD** is authored in the car's X-Z plane, so `rx = rz = 0` already IS
   *   flat and `R` collapses to `Ry(ry)` — a pure yaw. Its tumble stays in `ry`.
   * • **A DOOR** is authored thin in **Z**, spanning `floorY..belt` in **Y**,
   *   pivoted on the hinge at car `y = 0` (`carBuild.js#emitDoors`). At
   *   `rx = rz = 0` it therefore stands UPRIGHT ON ITS EDGE with its sill in
   *   the air — the shipped bug, visible on every wreck. Laying it on its face
   *   is `rx = ±π/2`, and then the ground spin must move to **`rz`**: with
   *   `R = Rx·Ry·Rz` a non-zero `ry` is applied INSIDE the flip and tilts the
   *   door's long axis back out of the ground plane, whereas `rz` is applied
   *   before it and stays in plane. Swap those two and the door lifts a corner.
   *
   * ⚠ AND THE SIGN IS THE PAINTED SIDE. `carBuild.js` puts a door's outer skin
   * at `+z` local for a RIGHT door and `−z` for a LEFT one, and `Rx(a)` maps
   * local `+z` to world `(0, −sin a, ...)` — so a right door needs `−π/2` and a
   * left door `+π/2` to land paint-up. This is the same trap `Breakables.js`
   * records for its bench planks (settle at an EVEN multiple of π, because the
   * underside was baked black): a panel that lands the wrong way up shows a
   * face nobody lit.
   *
   * @param {number} i Panel ring slot.
   */
  const settleAttitude = (i) => {
    const rx = qSetRX[i];
    if (rx !== 0) {
      const spin = qRY[i];
      qRX[i] = rx;
      qRY[i] = 0;
      qRZ[i] = spin;
      return;
    }
    qRX[i] = 0;
    qRZ[i] = 0;
  };

  /**
   * Ballistic + tumble, settling on the ground. Ground is sampled per step
   * while a panel is FALLING rather than once at launch: `blastToss` throws one
   * up to 9 m and this city has kerbs, so the launch-time sample that
   * `Breakables` gets away with (its pieces travel under 2 m) would bury half of
   * these in a pavement.
   * @param {number} dt
   */
  const stepParts = (dt) => {
    for (let i = 0; i < PART_CAP; i++) {
      if (qCar[i] < 0 || qRest[i]) continue;
      qVY[i] -= VEHICLE.gravity * dt;
      qX[i] += qVX[i] * dt;
      qY[i] += qVY[i] * dt;
      qZ[i] += qVZ[i] * dt;
      qRX[i] += qSX[i] * dt;
      qRY[i] += qSY[i] * dt;
      qRZ[i] += qSZ[i] * dt;
      if (qVY[i] < 0) {
        const floor = heightAt(qX[i], qZ[i]) + qLift[i];
        if (qY[i] <= floor) {
          qY[i] = floor;
          settleAttitude(i);
          qRest[i] = 1;
        }
      }
      if (fleet && typeof fleet.setPartPose === 'function') {
        fleet.setPartPose(qCar[i], qName[i], qX[i], qY[i], qZ[i], qRX[i], qRY[i], qRZ[i]);
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // particles
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Claims a ring slot. THE one write path — every particle in the game goes
   * through here, so the ring accounting exists once.
   *
   * @param {number} kind {@link CAR_FX}
   * @param {number} x @param {number} y @param {number} z
   * @param {number} vx @param {number} vy @param {number} vz m/s.
   * @param {number} life @param {number} r0 @param {number} grow
   * @param {number} tone Grey (smoke) or heat (fire), 0..1.
   */
  const push = (kind, x, y, z, vx, vy, vz, life, r0, grow, tone) => {
    const i = cNext;
    cNext = (cNext + 1) % PCAP;
    if (!cAlive[i]) cLive++;
    cAlive[i] = 1;
    cKind[i] = kind;
    cX[i] = x;
    cY[i] = y;
    cZ[i] = z;
    cVX[i] = vx;
    cVY[i] = vy;
    cVZ[i] = vz;
    cAge[i] = 0;
    cLife[i] = life;
    cR0[i] = r0 * (0.75 + rng() * 0.5);
    cGrow[i] = grow;
    cTone[i] = tone;
  };

  /**
   * A puff from a CONTINUOUS source — a bonnet, a burning shell. It climbs and
   * wanders; it has no direction of its own.
   *
   * @param {number} kind @param {number} x @param {number} y @param {number} z
   * @param {number} life @param {number} r0 @param {number} grow
   * @param {number} tone
   * @param {number} rise m/s up.
   * @param {number} drift ±m/s horizontally.
   */
  const emit = (kind, x, y, z, life, r0, grow, tone, rise, drift) => {
    const vx = (rng() * 2 - 1) * drift;
    const vz = (rng() * 2 - 1) * drift;
    push(kind, x, y, z, vx, rise, vz, life, r0, grow, tone);
  };

  /**
   * THE BANG. A short violent burst of fire filling {@link VEHICLE.blastR} and
   * dying over {@link VEHICLE.blastLife}, plus the smoke column it leaves.
   *
   * This is what separates the moment of detonation from a car that was already
   * burning: without it a blast's entire visual is the wreck's ordinary flame
   * starting up plus the panels leaving, and nothing says *bang*.
   *
   * ⚠ **It is emitted at the EVENT, in {@link destroy}, not in the step-5
   * emission pass** — the one exception to the step order, and it is a real
   * distinction rather than a shortcut. Step 5 emits from STATE: a rate derived
   * from the car's current hp, which can be re-derived on any later step. A
   * fireball is an EVENT with no state behind it, and deferring it would mean a
   * blast raised by `explode()` from outside `update` drew nothing until the
   * caller's next step — i.e. the flash would lag its own explosion.
   *
   * The puff count is a budget in bloom AND in ring slots; see
   * {@link VEHICLE.blastPuffs}.
   *
   * @param {CarRec} rec
   */
  const emitFireball = (rec) => {
    const R = VEHICLE.blastR;
    for (let k = 0; k < VEHICLE.blastPuffs; k++) {
      // A ball, biased UPWARD: an explosion climbs, and a symmetric sphere
      // spends half its puffs under the tarmac where nobody sees them.
      const a = rng() * Math.PI * 2;
      const up = 0.2 + rng() * 0.8;
      const flat = Math.sqrt(Math.max(0, 1 - up * up));
      const dx = Math.cos(a) * flat;
      const dz = Math.sin(a) * flat;
      const d = R * 0.3 * rng();
      // Seeded inside a third of the radius and EXPANDING to fill it: the ball
      // has to grow, or ten puffs placed at their final spread read as ten
      // separate flames rather than as one detonation.
      const speed = (R * 0.45) / VEHICLE.blastLife;
      push(
        CAR_FX.FIRE,
        rec.x + dx * d, rec.y + 0.6 + up * d, rec.z + dz * d,
        dx * speed, up * speed, dz * speed,
        VEHICLE.blastLife * (0.6 + rng() * 0.5),
        R * 0.3, R * 0.35,
        // Hot core, cooling at the edges — `heat` 1 is the yellow-white centre.
        1 - rng() * 0.35,
      );
    }
    for (let k = 0; k < VEHICLE.blastSmoke; k++) {
      const a = rng() * Math.PI * 2;
      const d = R * 0.25 * rng();
      push(
        CAR_FX.SMOKE,
        rec.x + Math.cos(a) * d, rec.y + 0.8, rec.z + Math.sin(a) * d,
        Math.cos(a) * 0.6, VEHICLE.puffRise * 1.6, Math.sin(a) * 0.6,
        VEHICLE.puffLife * 1.6, R * 0.28, VEHICLE.puffGrow * 1.5,
        // The blackest smoke in the game: this is burning fuel.
        SMOKE_GREY.blast,
      );
    }
  };

  /**
   * The bonnet, in world space: `L/2 − 0.35` ahead of the car's centre along its
   * heading, 0.9 m up. DERIVED from the chassis this system recorded, so a bus
   * smokes from a bus's bonnet and a hatchback from a hatchback's.
   *
   * Heading follows `docs/vehicles.md`: world yaw is `atan2(−fz, fx)`, so the
   * nose points at `(cos yaw, −sin yaw)`. Get the sign wrong and every car in
   * the city trails smoke out of its boot.
   *
   * @param {CarRec} rec @param {number[]} out `[x, y, z]`, reused scratch.
   */
  const bonnetOf = (rec, out) => {
    const ahead = rec.L / 2 - 0.35;
    out[0] = rec.x + Math.cos(rec.yaw) * ahead;
    out[1] = rec.y + 0.9;
    out[2] = rec.z - Math.sin(rec.yaw) * ahead;
    return out;
  };

  /** Reused by {@link emitFor} — this runs per damaged car per frame. */
  const scratch = [0, 0, 0];

  /**
   * Whatever this car is currently giving off.
   *
   * @param {CarRec} rec @param {number} dt
   */
  const emitFor = (rec, dt) => {
    const p = bonnetOf(rec, scratch);
    let smoke = 0;
    let fire = 0;
    let grey = SMOKE_GREY.hurt;
    let heat = 0.8;

    if (rec.wreck) {
      const t = rec.wreckT;
      if (t < VEHICLE.burnTime) {
        // The flame phase: full fire, and the blackest continuous smoke here.
        fire = VEHICLE.fireRate;
        smoke = VEHICLE.smokeRate * 1.5;
        grey = SMOKE_GREY.wreck;
        heat = 0.95 - 0.35 * (t / VEHICLE.burnTime);
      } else if (t < VEHICLE.burnTime + VEHICLE.smoulderTime) {
        // Smoulder: smoke only, thinning out, so a wreck goes quiet rather than
        // stopping dead on a frame boundary. It thins by ALPHA (the rate) and
        // pales a little as the fire under it dies — still far darker than a
        // car that is merely smoking, which is what keeps the ramp monotone.
        const u = (t - VEHICLE.burnTime) / VEHICLE.smoulderTime;
        smoke = VEHICLE.smokeRate * (1 - u) * 0.8;
        grey = SMOKE_GREY.smoulderFrom + (SMOKE_GREY.smoulderTo - SMOKE_GREY.smoulderFrom) * u;
      }
    } else {
      const f = rec.hp / rec.maxHp;
      if (f < VEHICLE.fireAt) {
        const s = clamp((VEHICLE.fireAt - f) / VEHICLE.fireAt, 0, 1);
        fire = VEHICLE.fireRate * (0.4 + 0.6 * s);
        smoke = VEHICLE.smokeRate * (0.9 + 0.7 * s);
        grey = SMOKE_GREY.burning;
      } else if (f < VEHICLE.smokeAt) {
        // ⚠ The 0.35 FLOOR is deliberate. A rate that ramped from zero at the
        // threshold would mean a car at 49% emits a puff every eight seconds,
        // i.e. crossing the line does nothing visible and the threshold reads as
        // arbitrary. It has to announce itself on the hit that crosses it.
        const s = clamp((VEHICLE.smokeAt - f) / (VEHICLE.smokeAt - VEHICLE.fireAt), 0, 1);
        smoke = VEHICLE.smokeRate * (0.35 + 0.65 * s);
        // DESCENDING with `s`: pale at the top of the band, darkening toward
        // the fire threshold. See SMOKE_GREY — this is the line that shipped
        // the wrong way round.
        grey = SMOKE_GREY.hurt + (SMOKE_GREY.failing - SMOKE_GREY.hurt) * s;
      }
    }

    if (smoke > 0) {
      // Clamped, not just accumulated: a caller that hands in a one-second dt
      // (a stall, a debug scrub) must not spin this loop a hundred times to
      // emit puffs that all die inside the same frame anyway.
      rec.smokeAcc = Math.min(rec.smokeAcc + smoke * dt, PUFF_BURST);
      while (rec.smokeAcc >= 1) {
        rec.smokeAcc -= 1;
        emit(CAR_FX.SMOKE, p[0], p[1], p[2], VEHICLE.puffLife, 0.42, VEHICLE.puffGrow, grey,
          VEHICLE.puffRise, VEHICLE.puffDrift);
        // A wreck smokes from the CENTRE as well as the bonnet — the fire is
        // the whole car by then, and one column off the nose reads as an engine
        // fire on an otherwise fine saloon.
        if (rec.wreck) {
          emit(CAR_FX.SMOKE, rec.x, rec.y + 0.7, rec.z, VEHICLE.puffLife * 1.2, 0.55,
            VEHICLE.puffGrow, grey, VEHICLE.puffRise * 0.85, VEHICLE.puffDrift);
        }
      }
    } else {
      rec.smokeAcc = 0;
    }

    if (fire > 0) {
      rec.fireAcc = Math.min(rec.fireAcc + fire * dt, PUFF_BURST);
      while (rec.fireAcc >= 1) {
        rec.fireAcc -= 1;
        emit(CAR_FX.FIRE, p[0], p[1], p[2], VEHICLE.fireLife, 0.34, 0.2, heat, 2.2, 0.35);
        if (rec.wreck) {
          emit(CAR_FX.FIRE, rec.x, rec.y + 0.55, rec.z, VEHICLE.fireLife, 0.46, 0.25, heat, 2.0, 0.35);
        }
      }
    } else {
      rec.fireAcc = 0;
    }
  };

  /**
   * Age every particle, then hand the survivors to `fx` in ONE
   * reset → add → commit pass — the `LightPools` idiom. A per-frame rebuild
   * beats a free list here for the same reason it does there: a puff is a pure
   * function of its own age, so there is no slot to leak.
   * @param {number} dt
   */
  const stepParticles = (dt) => {
    if (fx && typeof fx.reset === 'function') fx.reset();
    for (let i = 0; i < PCAP; i++) {
      if (!cAlive[i]) continue;
      cAge[i] += dt;
      if (cAge[i] >= cLife[i]) {
        cAlive[i] = 0;
        cLive--;
        continue;
      }
      cX[i] += cVX[i] * dt;
      cY[i] += cVY[i] * dt;
      cZ[i] += cVZ[i] * dt;
      const u = cAge[i] / cLife[i];
      const r = cR0[i] + cGrow[i] * cAge[i];
      if (!fx) continue;
      if (cKind[i] === CAR_FX.FIRE) {
        // A flame is brightest at birth and gone by the end — a linear fade
        // would leave a bright orange disc snapping off mid-frame.
        fx.addFire?.(cX[i], cY[i], cZ[i], r, 1 - u * u, cTone[i]);
      } else {
        // Smoke fades IN as well as out: a puff that appears at full opacity is
        // a sprite popping on.
        const a = Math.min(1, u * 6) * (1 - u) * 0.55;
        fx.addSmoke?.(cX[i], cY[i], cZ[i], r, a, cTone[i]);
      }
    }
    if (fx && typeof fx.commit === 'function') fx.commit();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // damage and the blast
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * @returns {number} The attacker id `EnemyManager#damage` should record —
   *   `factions.PLAYER` for the player's doing, −1 for anonymous. This ONE
   *   value is the whole difference between a blast that starts a manhunt and
   *   one the city shrugs at (see `docs/factions.md`).
   * @param {boolean} byPlayer
   */
  const srcOf = (byPlayer) => (byPlayer && factions ? (factions.PLAYER ?? -1) : -1);

  /**
   * Destroys a car: EXACTLY ONCE, in the documented order.
   * @param {CarRec} rec @param {boolean} byPlayer @param {number} depth
   */
  const destroy = (rec, byPlayer, depth) => {
    if (rec.wreck) return;
    rec.hp = 0;
    rec.wreck = true;
    rec.wreckT = 0;
    // Paired with `wreckT`, and set here rather than only at birth so the clock
    // and its one-shot can never be out of step. `destroy` returns early on a
    // car that is already a wreck, so this cannot re-arm a spent latch.
    rec.despawnAsked = false;
    rec.blame = rec.blame || byPlayer;

    // 1. the blast, queued. It is resolved by `drainBlasts` — from inside
    //    `update` that is step 3, and from a direct `explode()` it is
    //    immediately after this returns.
    queue.push({ x: rec.x, y: rec.y, z: rec.z, handle: rec.handle, byPlayer: rec.blame, depth });

    // 1b. THE BANG, now, on this frame. See `emitFireball` for why it is here
    //     and not in the step-5 emission pass.
    emitFireball(rec);

    // 2. the panels, all of them, thrown outward.
    tearAll(rec);

    // 3. charred, and the WINDOWS ARE GONE. Owner requirement: a destroyed car
    //    has no windscreen and no wheels. The wheels are panels and left with
    //    the rest of them in step 2; the glass is not thrown, because there is
    //    no shard geometry to throw — it is simply hidden.
    if (fleet && typeof fleet.setTint === 'function') {
      fleet.setTint(rec.handle, VEHICLE.charDay, VEHICLE.charNight);
    }
    if (fleet && typeof fleet.setPartHidden === 'function') {
      fleet.setPartHidden(rec.handle, GLASS, true);
      rec.glassOut = true;
    }

    // 3b. AND IT SITS DOWN. With the wheels off, a shell left at ride height
    //     floats on a band of daylight with its shadow adrift below it — the
    //     one thing that would look worse than the intact wheels did. The drop
    //     is MEASURED off this archetype (`rideHeightOf`), never tabled.
    rec.dropY = rideHeightOf(rec.handle);
    applyWreckDrop(rec);

    // 4/5. the wreck burns; `bus` tells the wiring the slot needs handing over.
    const p = { handle: rec.handle, x: rec.x, y: rec.y, z: rec.z, byPlayer: rec.blame };
    bus?.emit?.('car:wrecked', p);
    bus?.emit?.('car:exploded', { ...p });
  };

  /**
   * Resolves one blast: actors, the player, then other cars. Cars LAST so a
   * chain arrives after the people it was going to kill anyway have been
   * counted at this blast's own falloff, not at the next one's.
   * @param {{x: number, y: number, z: number, handle: number, byPlayer: boolean, depth: number}} b
   */
  const resolveBlast = (b) => {
    blastCount++;
    const R = VEHICLE.explodeRadius;
    const src = srcOf(b.byPlayer);

    // ── actors ──
    if (manager) {
      const top = manager.top | 0;
      for (let i = 0; i < top; i++) {
        if (!manager.isLive?.(i)) continue;
        const st = manager.state?.[i];
        if (st === STATE.DEAD || st === STATE.DYING) continue;
        const dx = manager.x[i] - b.x;
        const dz = manager.z[i] - b.z;
        // THREE dimensions, not two. Shop crowds stand 100 m under the city
        // (`docs/interiors.md`), and an XZ-only test would machine-gun a
        // grocer's every time a car went up on the pavement above him.
        const dy = (manager.y ? manager.y[i] : b.y) - b.y;
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const amt = falloffAt(VEHICLE.explodeDamage, d, R);
        if (amt <= 0) continue;
        // Knockback points AWAY from the blast; a body at the exact centre is
        // pushed along the car's +X, never along (0,0).
        const l = Math.hypot(dx, dz);
        manager.damage?.(i, amt, l > 1e-4 ? dx / l : 1, l > 1e-4 ? dz / l : 0, src);
      }
    }

    // ── the player ──
    const inCar = playerInCar?.() === true;
    const ownHandle = driving?.fleetHandle ?? -1;
    if (inCar && ownHandle >= 0 && ownHandle === b.handle) {
      // The car they are sitting in. There is no falloff from zero metres and
      // no honest way to survive it — this is the carjack-a-burning-car ending.
      //
      // ⚠ `lethal: true`, and the flag is what enforces it — NOT the 140. See
      // the `o.hurtPlayer` note above: armour is spent before health, so a vest
      // turns this into 40 hp and the player walks out of their own fireball.
      // The falloff branch below deliberately keeps the ordinary path, because
      // a vest SHOULD save you from a blast you were merely standing in.
      hurtPlayer?.(VEHICLE.explodeDamage, true);
    } else {
      const pp = playerPos?.() ?? null;
      if (pp) {
        const d = Math.sqrt((pp.x - b.x) ** 2 + ((pp.y ?? b.y) - b.y) ** 2 + (pp.z - b.z) ** 2);
        const amt = falloffAt(VEHICLE.explodeDamage, d, R);
        if (amt > 0) hurtPlayer?.(amt);
      }
    }

    // ── other cars: the chain ──
    for (const rec of cars.values()) {
      if (rec.handle === b.handle || rec.wreck) continue;
      const d = Math.sqrt((rec.x - b.x) ** 2 + (rec.y - b.y) ** 2 + (rec.z - b.z) ** 2);
      const amt = falloffAt(VEHICLE.explodeCarDamage, d, R);
      if (amt <= 0) continue;
      rec.hp -= amt;
      rec.blame = rec.blame || b.byPlayer;
      if (rec.hp <= 0) destroy(rec, b.byPlayer, b.depth + 1);
    }
  };

  /**
   * Drains the queue. Iterative and depth-capped — see the header.
   */
  const drainBlasts = () => {
    if (draining) return;
    draining = true;
    while (qHead < queue.length) {
      const b = queue[qHead++];
      // Past the cap the car is still destroyed (it was destroyed before it was
      // queued) — its blast simply does no damage, which is what stops a
      // pathological ring of cars from running forever.
      if (b.depth < VEHICLE.explodeMaxChain) resolveBlast(b);
    }
    queue.length = 0;
    qHead = 0;
    draining = false;
  };

  /**
   * @param {number} handle
   * @param {number} amount
   * @param {{kx?: number, kz?: number, byPlayer?: boolean}} [opt]
   * @returns {boolean} True when THIS hit destroyed the car. False for an
   *   untracked handle, a non-positive amount, a car that is already a wreck,
   *   and for a hit it survived.
   */
  const damage = (handle, amount, opt = {}) => {
    if (disposed || !(amount > 0)) return false;
    const rec = cars.get(handle);
    if (!rec || rec.wreck) return false;
    const byPlayer = opt.byPlayer === true;
    if (byPlayer) rec.blame = true;
    rec.hp -= amount;
    // A single big hit rips a panel off — before the destruction check, so the
    // panel that came off in the crash is thrown along the IMPACT and the rest
    // are thrown by the blast.
    if (amount >= VEHICLE.partTearMin) tearOne(rec, opt.kx ?? 0, opt.kz ?? 0);
    if (rec.hp > 0) return false;
    destroy(rec, byPlayer, 0);
    if (!stepping) drainBlasts();
    return true;
  };

  return {
    name: 'vehicles',

    /**
     * ONE fixed step. The order is the header's, and each of the five is a
     * function so the order is legible in one screen.
     * @param {number} dt
     */
    update(dt) {
      if (disposed) return;
      stepping = true;
      try {
        // 1 ── who exists
        reconcile();
        if (!(dt > 0)) return;

        // 1b ── who is under a moving car. Reads the pose delta step 1 just
        //       established, and nothing else; see the header for why it sits
        //       here rather than below the blasts.
        runOver(dt);

        // 2 ── fire is terminal
        for (const rec of cars.values()) {
          if (rec.wreck) {
            rec.wreckT += dt;
            // Re-assert the shell's drop against whoever last wrote its
            // transform. Idempotent by construction — see `applyWreckDrop`.
            applyWreckDrop(rec);
            // ...and a wreck does not stand there forever. The clock runs from
            // DETONATION, not from the fire going out, so `wreckLife` is one
            // number the player can feel rather than the sum of three phases.
            //
            // ⚠ WE DO NOT FORGET THE RECORD HERE, and that is the same rule as
            // `'car:wrecked'` (see the header): the wiring removes the car, the
            // fleet's serial goes to 0, and `reconcile` drops the record on the
            // next pass on the FLEET'S word. Forgetting it ourselves would
            // re-mint this very car at `maxHp` on the next pass, because the
            // traffic poll is still presenting it — precisely the self-healing
            // bug the serial gate was added to close. The latch is what keeps
            // the ask to exactly one while we wait to be believed.
            if (!rec.despawnAsked && rec.wreckT >= VEHICLE.wreckLife) {
              rec.despawnAsked = true;
              bus?.emit?.('car:despawn', { handle: rec.handle, x: rec.x, y: rec.y, z: rec.z });
            }
            continue;
          }
          if (rec.hp / rec.maxHp >= VEHICLE.fireAt) continue;
          rec.hp -= VEHICLE.burnDps * dt;
          if (rec.hp <= 0) destroy(rec, rec.blame, 0);
        }

        // 3 ── the chain
        drainBlasts();

        // 4 ── the panels
        stepParts(dt);

        // 5 ── the smoke
        for (const rec of cars.values()) emitFor(rec, dt);
        stepParticles(dt);
      } finally {
        stepping = false;
      }
    },

    /** @param {number} handle @returns {number} hp, or −1 when untracked. */
    hpOf(handle) {
      const rec = cars.get(handle);
      return rec ? rec.hp : -1;
    },
    /** @param {number} handle @returns {number} */
    maxHpOf(handle) {
      const rec = cars.get(handle);
      return rec ? rec.maxHp : -1;
    },
    /** @param {number} handle @returns {number} 0..1, or −1 when untracked. */
    fracOf(handle) {
      const rec = cars.get(handle);
      if (!rec) return -1;
      return clamp(rec.hp / rec.maxHp, 0, 1);
    },
    /** @param {number} handle @returns {boolean} */
    isWreck(handle) {
      return cars.get(handle)?.wreck === true;
    },

    damage,

    /**
     * DROPS a car's record: panels handed back, particles' owner gone, health
     * forgotten. The next {@link update} that still sees the handle mints a
     * fresh car at {@link VEHICLE.maxHp}.
     *
     * ⚠ This exists because absence stopped being a rebuild mechanism. Hiding a
     * car from the poll for one step used to drop its record as a side effect,
     * and `dev/scenes/cardamage.js` uses exactly that to rebuild its exhibits —
     * but the same path was also silently healing cars that blinked out by
     * accident (see {@link reconcile}). The two cases are indistinguishable
     * from in here: in both, the fleet still holds the car. So the deliberate
     * one now has to SAY so, and this is the door.
     *
     * @param {number} handle
     * @returns {boolean} False when the handle held no tracked car.
     */
    release(handle) {
      if (disposed) return false;
      const rec = cars.get(handle);
      if (!rec) return false;
      forget(rec);
      return true;
    },

    /**
     * Damages the nearest tracked car to a point — the door for gunfire, which
     * resolves against actors and has no car handle to hand us (`Gameplay`
     * knows where a round went, not what it hit).
     *
     * WRECKS ARE SKIPPED, and that is the point rather than an optimisation: a
     * burnt-out shell parked between the player and a live car would otherwise
     * swallow every round aimed past it.
     *
     * @param {number} x @param {number} z @param {number} amount
     * @param {{radius?: number, byPlayer?: boolean, kx?: number, kz?: number}} [opt]
     * @returns {number} The handle hit, or −1.
     */
    damageAt(x, z, amount, opt = {}) {
      if (disposed || !(amount > 0)) return -1;
      const r = opt.radius ?? 3;
      let best = -1;
      let bestD = r * r;
      for (const rec of cars.values()) {
        if (rec.wreck) continue;
        const d2 = (rec.x - x) ** 2 + (rec.z - z) ** 2;
        if (d2 > bestD) continue;
        bestD = d2;
        best = rec.handle;
      }
      if (best < 0) return -1;
      damage(best, amount, opt);
      return best;
    },

    /**
     * Forces the blast now. Used by the wiring (a scripted mission car), by a
     * chain, and by the suite.
     *
     * ⚠ "Now" means the damage lands before this returns — UNLESS we are inside
     * {@link update}, where the documented step order owns when blasts resolve.
     * @param {number} handle
     * @param {{byPlayer?: boolean}} [opt]
     */
    explode(handle, opt = {}) {
      if (disposed) return;
      const rec = cars.get(handle);
      if (!rec || rec.wreck) return;
      destroy(rec, opt.byPlayer === true, 0);
      if (!stepping) drainBlasts();
    },

    /**
     * The seam `systems/Driving.js` calls, once per impact per step.
     *
     * @param {'wall'|'car'} kind
     * @param {number} dv m/s the impact took off the body. Always > 0.
     * @param {number} handle Fleet handle of the car that HIT something.
     * @param {number} [otherIdx] `Traffic` DRIVING slot of the car that was hit,
     *   or −1 for a wall.
     * @returns {boolean} True when the impact destroyed anything.
     */
    onImpact(kind, dv, handle, otherIdx = -1) {
      if (disposed || !(dv > 0)) return false;
      // ⚠ Every car this seam can name is the PLAYER's — `Driving` only
      // integrates two bodies, the one under the player and the one they bailed
      // out of, and the coasting one is rolling because of something they did.
      // AI traffic never reports an impact (an AI car is an arc length on a
      // lane, it cannot leave the road), so there is no third case to get wrong.
      const byPlayer = true;
      let killed = false;
      if (kind === 'wall') {
        const amt = Math.max(0, dv - VEHICLE.wallImpactMin) * VEHICLE.wallDamage;
        if (amt > 0) killed = damage(handle, amt, { byPlayer });
        return killed;
      }
      const amt = Math.max(0, dv - VEHICLE.carImpactMin) * VEHICLE.carDamage;
      if (amt <= 0) return false;
      // BOTH cars. A shunt is one impact and two victims — the geometry does
      // not care which of them had their foot down.
      killed = damage(handle, amt, { byPlayer }) || killed;
      const other = otherIdx >= 0 ? (traffic?.handle?.[otherIdx] ?? -1) : -1;
      if (other >= 0 && other !== handle) killed = damage(other, amt, { byPlayer }) || killed;
      return killed;
    },

    /**
     * THE SETTLE CONVENTION, published — the pose a torn panel comes to rest
     * in, ready to hand straight to `CarFleet#setPartPose`.
     *
     * ⚠ It is exported for ONE reason and it is not convenience. `/play/
     * cardamage` poses two panels by hand (the real tear picks its panel with
     * an rng draw, so a review wall driven through it would show the bonnet off
     * about two visits in five, and the reviewer needs it off every time). That
     * hand pose has to AGREE with this one, or the wall's two examples of a
     * torn panel disagree with each other — which is the exact disagreement the
     * reviewer is standing there to catch, pre-broken. It already went wrong
     * once the other way: the scene kept its own copy referring to two `VEHICLE`
     * constants, those constants were deleted when the lift became derived, and
     * the copy silently started posing panels at `NaN`.
     *
     * @param {number} handle @param {string} name Part name.
     * @param {number} [spin=0] Ground spin, radians. It is routed to whichever
     *   channel this kind's flip leaves horizontal — `ry` for a hood, `rz` for
     *   anything laid on its face — because with `R = Rx·Ry·Rz` a leftover `ry`
     *   inside a ±π/2 flip tilts the panel back out of the ground plane.
     * @returns {{lift: number, rx: number, ry: number, rz: number}} `lift` is
     *   metres ABOVE the ground the origin sits, so the caller writes
     *   `heightAt(x, z) + lift`.
     */
    settlePose(handle, name, spin = 0) {
      const s = settleFor(handle, name);
      return s.rx === 0
        ? { lift: s.lift, rx: 0, ry: spin, rz: 0 }
        : { lift: s.lift, rx: s.rx, ry: 0, rz: spin };
    },

    /**
     * @returns {{tracked: number, smoking: number, burning: number,
     *   wrecks: number, particles: number, detached: number, blasts: number}}
     *   `smoking` and `burning` count CARS, not particles: a live car under
     *   {@link VEHICLE.smokeAt} is smoking (a burning one smokes too, so it is
     *   counted in both), and a wreck still in its flame phase is burning.
     *   `blasts` is a lifetime total.
     */
    stats() {
      let smoking = 0;
      let burning = 0;
      let wrecks = 0;
      for (const rec of cars.values()) {
        if (rec.wreck) {
          wrecks++;
          if (rec.wreckT < VEHICLE.burnTime) burning++;
          continue;
        }
        const f = rec.hp / rec.maxHp;
        if (f < VEHICLE.smokeAt) smoking++;
        if (f < VEHICLE.fireAt) burning++;
      }
      return { tracked: cars.size, smoking, burning, wrecks, particles: cLive, detached: qLive, blasts: blastCount };
    },

    /**
     * Idempotent. Everything tracked is forgotten, every panel is handed back,
     * every particle is dropped, and every injected reference is released —
     * this system outlives nothing, and a fleet it still pointed at after a
     * level teardown is a fleet that cannot be garbage collected.
     */
    dispose() {
      if (disposed) return;
      for (const rec of [...cars.values()]) forget(rec);
      cars.clear();
      queue.length = 0;
      qHead = 0;
      qCar.fill(-1);
      qSerial.fill(-1);
      qLive = 0;
      cAlive.fill(0);
      cLive = 0;
      traffic = null;
      driving = null;
      fleet = null;
      fx = null;
      manager = null;
      bus = null;
      factions = null;
      disposed = true;
    },
  };
}
